/*
 * Tickets + raffle integration  ·  assets/hoh-tickets.js
 *
 * What this owns
 * --------------
 * Everything client-side around the new ticket economy. The section
 * stays minimal: it just calls window.hohOnTicketResult(ticketJson)
 * when increment_hearts_capped returns, and this module handles:
 *   - The header daily counter ("1/2" with the rainbow ticket icon)
 *   - The "+1 ticket earned" celebration burst
 *   - State refresh from get_my_raffle_state
 *   - Exposing helpers the Roll Call and Raffle modules also use
 *
 * State lives in window.hohTicketState as a plain object so other
 * modules can read it without re-fetching:
 *   { tickets_today, tickets_today_max, hearts_given_today,
 *     roll_call_completed, pending_tickets, my_characters[], last_winner }
 *
 * Refresh is opportunistic: we update the cache on every heart and
 * on every Raffle/Roll Call modal open. No polling.
 */
(function(){
  'use strict';

  var STATE_KEY = '__hohTicketState';
  /* R64: Unspent ticket wallet. Tickets earned from daily activity
     (wander milestones + bounty) accumulate here as a SPENDABLE
     inventory the user actively distributes to characters they own.
     Tickets DO NOT EXPIRE — they stack across days until applied.
     The submission ticket is excluded (it auto-pins to the new
     character server-side), so per day the user banks at most
     3 distributable tickets. localStorage-backed so a tab reload
     preserves the count; the server-side counterpart lives on
     hohTicketState.unspent_tickets when get_my_raffle_state is
     extended to return it. */
  var UNSPENT_KEY = 'hoh-ticket-unspent-v1';
  /* R793 (Jan, 2026-09-22): tickets are no longer earned and held. Each of
     your own heroes takes one ticket a day, given on its own page in the
     main Hall through hoh_hall_ticket_apply. So in the live Hall this
     per-device wallet reads empty, never moves, and nothing grants into it
     (dailies, Explore milestones and submission all stop). Legacy mode
     (the R793B doors not installed yet) keeps the wallet exactly as it was. */
  function r793Live(){
    try { return (typeof window.hohR793Live === 'function') && window.hohR793Live(); } catch(_){ return false; }
  }
  function getUnspent(){
    if(r793Live()) return 0;
    try {
      var raw = localStorage.getItem(UNSPENT_KEY);
      var n = parseInt(raw || '0', 10);
      return isNaN(n) || n < 0 ? 0 : n;
    } catch(_){ return 0; }
  }
  function setUnspent(n){
    if(r793Live()) return;
    var clamped = Math.max(0, n|0);
    try { localStorage.setItem(UNSPENT_KEY, String(clamped)); } catch(_){}
    window.hohTicketState.unspent_tickets = clamped;
    try { document.dispatchEvent(new CustomEvent('hoh:unspent-tickets-changed', { detail: { count: clamped } })); } catch(_){}
  }
  /* Public helpers — every earn surface (wander, bounty) calls
     hohAddUnspentTicket(). Apply Ticket UI calls hohSpendUnspentTicket. */
  window.hohGetUnspentTickets = getUnspent;
  /* R94: Server-side daily cap gate for multi-device exploit fix.
     Before granting any ticket via the wallet, check the SERVER's
     authoritative tickets_today count (synced via fetchRaffleState
     and refreshed periodically). If the server says we've already
     hit the daily cap (today's tickets_today >= tickets_today_max),
     refuse the local grant. Without this gate, a user logged in on
     two devices could earn the daily max on each separately because
     localStorage is per-device.
     The wallet refund path (hohRefundUnspentTicket) intentionally
     bypasses this check — refunds need to land regardless of cap. */
  /* R140-Z3o: SERVER-AUTHORITATIVE daily ticket cap. Cortez's
     exploit (clear cache → re-earn all tickets, infinite farm) is
     possible because the OLD path used localStorage claim flags via
     computeDailyTotal as the gate. Clearing storage wipes the flags
     and the gate believes nothing has been claimed.
     The fix calls the new `grant_daily_ticket` RPC FIRST — the
     server enforces the cap against the `ticket_grants_log` table
     keyed by (email, ymd). Even with a wiped localStorage, the
     server says "no, you already grabbed 4 today" and refuses.
     For anonymous (no email) users the server can't track them, so
     their wallet stays local-only (matches existing behavior — anon
     tickets don't persist anyway).
     The function returns a Promise<boolean>. Callers that previously
     used the boolean return synchronously (e.g. inside the legshop
     ticket trade) MUST be updated to await this. */
  window.hohAddUnspentTicket = function(n, reasonKey){
    /* R793: no ticket is earned anywhere in the live Hall, and no
       grant_daily_ticket call is made. Callers treat false as "not granted". */
    if(r793Live()) return false;
    var add = Math.max(1, n || 1);
    var reason = (reasonKey && typeof reasonKey === 'string') ? reasonKey : 'unknown';

    /* Read signed-in email from the auth token. If none, fall back to
       the legacy local-only flow (anon users' wallets don't persist
       so the exploit doesn't apply). */
    var email = null;
    try {
      var raw = localStorage.getItem('hoh-your-hall-token');
      if(raw){
        var t = JSON.parse(raw);
        if(t && t.email) email = String(t.email).trim().toLowerCase();
      }
    } catch(_){}

    function applyLocalBump(){
      setUnspent(getUnspent() + add);
      try {
        if(window.hohTicketState && typeof window.hohTicketState.tickets_today === 'number'){
          var maxNow = (window.hohTicketState.tickets_today_max > 0) ? window.hohTicketState.tickets_today_max : 5;
          window.hohTicketState.tickets_today = Math.min(maxNow, (window.hohTicketState.tickets_today || 0) + add);
        }
      } catch(_){}
    }

    /* v38: it said "daily ticket cap" for every refusal including ones that
       were not caps at all, and it said it through window.hohShowHeartToast,
       which is defined NOWHERE in this theme. Ten call sites across nine files
       reach for that function and every one is guarded, so every one is
       silent. hohShowHallToast is the one that actually exists. */
    function showCapToast(reason){
      var msg = (reason === 'cap_hit' || !reason)
        ? 'You have already earned 4 tickets today. Tomorrow resets.'
        : 'That ticket did not stick. The Hall said: ' + reason;
      try { if(typeof window.hohShowHeartToast === 'function'){ window.hohShowHeartToast('Daily ticket cap', msg); return; } } catch(_){}
      try { if(typeof window.hohShowHallToast === 'function'){ window.hohShowHallToast(msg); return; } } catch(_){}
      try { console.warn('[HOH tickets] ' + msg); } catch(_){}
    }

    /* v38: A REFUND IS NOT AN EARN. Putting back a ticket the player already
       owned, after a run failed to start or a pack purchase fell over, was
       being counted against the same 4-a-day ceiling as earning a new one. So
       a player near the cap paid for a failure twice, once by losing the run
       and again by losing the ticket. Refunds never touch the server ledger. */
    if(reason === 'refund' || reason.indexOf('refund') === 0){
      applyLocalBump();
      return true;
    }

    /* Anonymous user — local-only wallet (existing behavior). */
    if(!email){
      applyLocalBump();
      return true;
    }

    /* Signed-in user — gate on the server RPC. The RPC inserts a row
       in `ticket_grants_log` atomically; concurrent requests can't
       double-grant. Returns { ok:false, reason:'cap_hit' } when the
       user already has 4 grants today. */
    /* v35: THIS GUARD ALWAYS FAILED, so the server cap below has never once
       run. Bare `supabase` resolves to window.supabase, which is the
       supabase-js LIBRARY namespace from the CDN — it owns createClient and
       has no .rpc, so every signed-in grant took the "client missing" exit and
       the 4-a-day ceiling stayed a client-side suggestion. The real client is
       window.hohSupabaseClient (hoh-section-core builds it into a local var).
       hoh-your-hall.js:2916 hit this exact bug in hohGrantCharacterXp and fixed
       it the same way; this file never got the same treatment. */
    var sb = window.hohSupabaseClient;
    if(!sb || typeof sb.rpc !== 'function'){
      /* genuinely no client yet (the roster has not loaded) — fall back to
         local-only rather than blocking a legitimate earn. */
      applyLocalBump();
      return true;
    }

    /* The RPC is async. Returning a synchronous boolean would break
       the contract for sync callers, so we return a thenable that
       resolves to bool but ALSO bumps locally on success. Sync
       callers that used `var ok = hohAddUnspentTicket(1)` and
       checked the value will now see a Promise — but our internal
       callers in this codebase all use `if(window.hohAddUnspentTicket(1) !== false)`
       and that check still works for the Promise (a Promise is
       truthy), so the apparent behavior matches the old success
       path. To be SAFE for hot paths, we ALSO do the local bump
       optimistically and roll it back if the RPC rejects. */
    applyLocalBump();
    return sb.rpc('grant_daily_ticket', {
      p_hearter_email: email,
      p_reason: reason
    }).then(function(res){
      var data = res && res.data;
      /* v38 ROOT CAUSE OF CORTEZ'S "only one ticket in two days", and the v35
         fix one screen up is what introduced it.
         Before v35 this guard read the bare `supabase` global, which is the
         library namespace and has no .rpc, so the call NEVER RAN and the local
         bump always stood. v35 pointed it at the real client, which switched on
         a code path whose failure branch DELETES the ticket.
         That branch is wrong because supabase-js does not reject on a database
         or PostgREST error. It RESOLVES with { data: null, error: {...} }. So
         the .catch below has never fired for a missing function, a permission
         error, or a schema-cache miss. Every one of those landed in `!ok`,
         rolled the ticket back, and told the player they had hit a daily cap
         they had not touched.
         The rule now: ONLY the server saying no is a no. A server that did not
         answer is not a refusal, and an earn is never destroyed by a transport
         failure. The cost of a wrongly kept ticket is one ticket. The cost of a
         wrongly deleted one is a player who stops believing the counter. */
      var err = res && res.error;
      if(err || data == null || typeof data !== 'object'){
        try {
          console.warn('[HOH tickets] grant_daily_ticket did not answer, keeping the earn. ' +
            (err ? ((err.code ? err.code + ' ' : '') + (err.message || '')) : 'no payload returned'));
        } catch(_){}
        return true;
      }
      if(data.ok === true) return true;

      /* a real, explicit refusal. Give the ticket back and name which one. */
      try { setUnspent(getUnspent() - add); } catch(_){}
      try {
        if(window.hohTicketState && typeof window.hohTicketState.tickets_today === 'number'){
          window.hohTicketState.tickets_today = Math.max(0, (window.hohTicketState.tickets_today || 0) - add);
        }
      } catch(_){}
      showCapToast(String(data.reason || ''));
      return false;
    }).catch(function(e){
      /* a genuine rejection, which for supabase-js means the request itself
         never completed. Same rule: keep the earn. */
      try { console.warn('[HOH tickets] grant_daily_ticket threw, keeping the earn. ' + ((e && e.message) || e)); } catch(_){}
      return true;
    });
  };
  window.hohSpendUnspentTicket = function(){
    var have = getUnspent();
    if(have <= 0) return false;
    setUnspent(have - 1);
    /* R128-A: Track applied-today so the wallet-recovery pass can
       distinguish a bug-shorted wallet from a wallet that's low because
       the user spent tickets. Without this counter, recovery would
       mistakenly top up users who legitimately drained their wallet. */
    try { bumpWalletApplied(1); } catch(_){}
    return true;
  };
  /* Refund path — called when an apply-ticket RPC fails. We optimistic-
     decrement on click, then re-add if the server rejects. */
  window.hohRefundUnspentTicket = function(){
    setUnspent(getUnspent() + 1);
    /* R128-A: Undo the applied bump for the failed apply so the
       counter stays accurate. */
    try { bumpWalletApplied(-1); } catch(_){}
  };

  /* R127-A: Per-day wallet-credit ledger. Keys claim-IDs that have
     already deposited a ticket into the wallet so a reconcile pass can
     detect earned-but-uncredited claims and backfill them. The ledger
     is keyed by ISO date (YYYY-MM-DD), so it auto-resets at midnight.
     Slots: 'wander_20', 'wander_40', 'bounty'. (Submit auto-pins
     server-side, never deposits to the wallet.)
     Why a ledger and not a simple counter: a counter can't tell us
     WHICH earn paths have been wallet-credited, so it can't backfill
     specific gaps. With per-slot booleans, the reconcile pass becomes
     "for each earned slot today not in the ledger, credit and mark." */
  function todayKey(){
    var d = new Date();
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + dd;
  }
  var WALLET_LEDGER_KEY_PREFIX = 'hoh-wallet-credited-';
  function readWalletLedger(){
    try {
      var raw = localStorage.getItem(WALLET_LEDGER_KEY_PREFIX + todayKey());
      if(!raw) return {};
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch(_){ return {}; }
  }
  function writeWalletLedger(ledger){
    try {
      localStorage.setItem(WALLET_LEDGER_KEY_PREFIX + todayKey(), JSON.stringify(ledger || {}));
    } catch(_){}
  }
  /* R128-A: Per-day applied-today counter. Tracks how many wallet
     tickets the user has spent (applied to characters) today. The
     wallet-recovery pass at seed time uses this to compute the
     EXPECTED net wallet change today: (earned − applied). If actual
     wallet < expected, that's a credit gap → top up.
     Without this counter, recovery couldn't tell a bug-shorted
     wallet from a wallet drained by legitimate spending. */
  var WALLET_APPLIED_KEY_PREFIX = 'hoh-wallet-applied-';
  function readWalletApplied(){
    try {
      var raw = localStorage.getItem(WALLET_APPLIED_KEY_PREFIX + todayKey());
      var n = parseInt(raw || '0', 10);
      return (isNaN(n) || n < 0) ? 0 : n;
    } catch(_){ return 0; }
  }
  function writeWalletApplied(n){
    try { localStorage.setItem(WALLET_APPLIED_KEY_PREFIX + todayKey(), String(Math.max(0, n|0))); } catch(_){}
  }
  function bumpWalletApplied(delta){
    var d = parseInt(delta, 10) || 0;
    var next = Math.max(0, readWalletApplied() + d);
    writeWalletApplied(next);
  }
  /* Mark a slot as credited. Idempotent — safe to call from any path
     that knows it just credited the wallet for that slot. */
  window.hohMarkWalletCredited = function(slot){
    if(!slot) return;
    var l = readWalletLedger();
    if(!l[slot]){
      l[slot] = true;
      writeWalletLedger(l);
    }
  };
  /* R127-A: Seed the ledger from CURRENT claim flags WITHOUT crediting.
     Used on init so we don't over-credit users who already have
     wallet credits from this session/day before the ledger existed.
     The seed says "these slots are considered already credited — skip
     them on backfill". After seed, claim handlers add to the ledger
     when they credit, and any genuine post-seed gap will be backfilled
     by the reconcile pass.
     Idempotent — re-seeding doesn't undo any later claim-handler marks
     (we only ADD to the ledger, never remove). */
  var ledgerSeeded = false;
  function seedWalletLedgerFromClaims(){
    if(ledgerSeeded) return;
    try {
      var dt = (typeof computeDailyTotal === 'function') ? computeDailyTotal() : null;
      if(!dt){ ledgerSeeded = true; return; }
      var ledger = readWalletLedger();
      /* Ticket slots are wander_20/40/60. These are slot NAMES, not
         heart thresholds — see the R406 note in hoh-fresh-faces.js.
         Daily wander cap stays 3 tickets. */
      if(dt.wanderClaimed && dt.wanderClaimed.m20) ledger.wander_20 = true;
      if(dt.wanderClaimed && dt.wanderClaimed.m40) ledger.wander_40 = true;
      if(dt.wanderClaimed && dt.wanderClaimed.m60) ledger.wander_60 = true;
      writeWalletLedger(ledger);
      /* R128-A: One-time-per-day wallet recovery. Compares the wallet
         to what TODAY's claim flags + applied counter say it should be.
         If the wallet is BELOW (earned − applied), that's a credit gap
         from a prior bug — top up the difference.
         Gated by a per-day "recovered" flag so this can't double-fire
         within the same day (e.g., page reload after recovery). The
         flag auto-resets at midnight along with the rest of the
         per-day localStorage keys. */
      try {
        var recoveryKey = 'hoh-wallet-recovered-' + todayKey();
        if(localStorage.getItem(recoveryKey) !== '1'){
          /* Ticket slots are wander_20/40/60 (slot names, not thresholds). */
          var earnedToday = (ledger.wander_20?1:0) + (ledger.wander_40?1:0) + (ledger.wander_60?1:0);
          var appliedToday = readWalletApplied();
          var expectedNetIncrease = Math.max(0, earnedToday - appliedToday);
          var currentWallet = getUnspent();
          /* R140-Q: Cross-device duplicate-ticket guard. The recovery
             pass was designed for "user earned tickets via claim flag
             but the wallet write didn't land" — a single-device bug.
             It uses readWalletApplied() (per-device localStorage) to
             compute appliedToday, which is WRONG when the user has
             applied tickets on a DIFFERENT device today.
             Concrete bug the user reported: applied 3 tickets on
             desktop, opened mobile, recovery saw earnedToday=3,
             appliedToday=0 (mobile is fresh, never applied here),
             topped up the wallet to 3, user re-applied 3 more,
             character ended up with 6 tickets from this user instead
             of 3.
             The signal that we're on a cross-device fresh load: BOTH
             appliedToday AND currentWallet are 0. A user who has
             actively engaged with tickets today on THIS device would
             have at least one of those non-zero.
             When that signal fires, refuse to top up. The wallet
             stays at 0 — which is correct if the user already
             applied on another device. If the rare "earned but
             never banked" case applies (user EARNED but never
             applied or banked, both counters at 0), they can
             manually claim from the Daily Tickets modal. The
             trade-off favors correctness over auto-magic recovery. */
          var crossDeviceFresh = (appliedToday === 0 && currentWallet === 0);
          if(currentWallet < expectedNetIncrease && !crossDeviceFresh){
            var deficit = expectedNetIncrease - currentWallet;
            setUnspent(currentWallet + deficit);
            try {
              if(typeof window.hohShowHeartToast === 'function'){
                window.hohShowHeartToast('Wallet topped up', 'Found ' + deficit + ' ticket' + (deficit === 1 ? '' : 's') + ' you earned today that hadn\'t banked.');
              }
            } catch(_){}
          }
          try { localStorage.setItem(recoveryKey, '1'); } catch(_){}
        }
      } catch(_){}
      ledgerSeeded = true;
    } catch(_){ ledgerSeeded = true; }
  }
  /* Reconcile pass: walks the day's earned slots (from claim flags +
     server bounty flag) and credits the wallet for any that haven't
     been logged in the ledger yet. Called after every state change so
     a missed credit self-heals on the next state read.
     Pre-seeded by seedWalletLedgerFromClaims on init so we don't
     retroactively top up users whose wallet predates this fix. */
  function reconcileWalletFromClaims(){
    try {
      /* Make sure init seed has run before any backfill logic. */
      if(!ledgerSeeded) seedWalletLedgerFromClaims();
      var dt = (typeof computeDailyTotal === 'function') ? computeDailyTotal() : null;
      if(!dt) return;
      var ledger = readWalletLedger();
      var changed = false;
      var added = 0;
      /* Ticket slots reconcile at wander_20/40/60. The heart-bonus
         slots (wander_10/30/50) are NOT ticket sources. */
      if(dt.wanderClaimed && dt.wanderClaimed.m20 && !ledger.wander_20){
        ledger.wander_20 = true; changed = true; added += 1;
      }
      if(dt.wanderClaimed && dt.wanderClaimed.m40 && !ledger.wander_40){
        ledger.wander_40 = true; changed = true; added += 1;
      }
      if(dt.wanderClaimed && dt.wanderClaimed.m60 && !ledger.wander_60){
        ledger.wander_60 = true; changed = true; added += 1;
      }
      if(changed){
        writeWalletLedger(ledger);
        if(added > 0){
          setUnspent(getUnspent() + added);
          try {
            if(window.hohTicketState && typeof window.hohTicketState.tickets_today === 'number'){
              var maxN = (window.hohTicketState.tickets_today_max > 0) ? window.hohTicketState.tickets_today_max : 5;
              window.hohTicketState.tickets_today = Math.min(maxN, (window.hohTicketState.tickets_today || 0) + added);
            }
          } catch(_){}
        }
      }
    } catch(_){}
  }
  window.hohReconcileWalletFromClaims = reconcileWalletFromClaims;
  window.hohSeedWalletLedgerFromClaims = seedWalletLedgerFromClaims;

  /* Sync the wallet count into the state object on init so consumers
     reading hohTicketState.unspent_tickets see the persisted value
     immediately. */
  try {
    if(window.hohTicketState){
      window.hohTicketState.unspent_tickets = getUnspent();
    }
  } catch(_){}
  window.hohTicketState = window.hohTicketState || {
    tickets_today: 0,
    unspent_tickets: getUnspent(),
    /* R61: bumped 2 → 4 to match the actual cap (1 submit + 2 wander
       + 1 bounty = 4). The old default was vestigial and could cap
       optimistic local writes at 2 before the server reply. */
    tickets_today_max: 4,
    submitted_today: false,
    submitted_today_max: 1,
    tinder_today: false,
    daily_total: 0,
    daily_max: 4,
    hearts_given_today: 0,
    roll_call_completed: false,
    pending_tickets: 0,
    my_characters: [],
    last_winner: null
  };

  /* R61: Compute the daily total from CLAIMED quests, not from raw
     progress. The previous version derived wander tickets from the
     heart count (hearts>=10 ? 1 : 0) + (hearts>=20 ? 1 : 0). After
     the R56 click-to-claim refactor that was wrong on three counts:
       1. Thresholds moved to 20 / 40 (the 60 milestone is a heart
          bonus, NOT a ticket — it caps wander at 2 tickets).
       2. The user has to CLICK the milestone pip to bank a ticket.
          Auto-crediting on heart bump credits before the user has
          earned anything.
       3. The count fluctuated as hearts moved up over the day.

     New model: read the actual claim flags via
     hohGetWanderQuestClaimed (date-keyed in localStorage so they
     auto-reset at midnight). Single source of truth.

     Sources per quest:
       Submit: server flag (st.submitted_today) OR local helper
       Wander: count of CLAIMED ticket milestones (max 3)
     Total max stays 4 (1 submit + 3 wander). R140-Z removed the
     Daily Bounty as a ticket source. */
  function computeDailyTotal(){
    var st = window.hohTicketState;
    /* Submitted: server-authoritative, fall back to local helper. */
    var submitted = !!st.submitted_today;
    if(!submitted && typeof window.hohHasSubmittedToday === 'function'){
      try { submitted = !!window.hohHasSubmittedToday(); } catch(_){}
    }
    /* R140-Z: Bounty removed as a ticket source. The daily ladder is
       now: 1 submit + 3 wander = 4 tickets max. */
    /* Wander: CLAIMED milestones only. 10 / 20 / 40 give tickets;
       60 gives +30 hearts so it doesn't add to ticket count.
       Falls back to 0 if fresh-faces isn't loaded yet — the local
       claim flags are the ONLY truth here. We intentionally do NOT
       fall back to the binary tinder flag because that legacy flag
       got set by old daily-tinder logic and would over-credit. */
    var wanderTickets = 0;
    var wanderClaimed = null;
    if(typeof window.hohGetWanderQuestClaimed === 'function'){
      try {
        wanderClaimed = window.hohGetWanderQuestClaimed();
        wanderTickets = (wanderClaimed && typeof wanderClaimed.count === 'number') ? wanderClaimed.count : 0;
      } catch(_){ wanderTickets = 0; }
    }
    /* Tinder flag for the tooltip — used to suppress the "Explore the
       Hall" hint once all wander tickets are claimed. */
    var tinder = wanderTickets >= 3;
    return {
      total: (submitted ? 1 : 0) + wanderTickets,
      max: 4,
      submitted: submitted,
      tinder: tinder,
      bounty: false,
      wanderTickets: wanderTickets,
      wanderClaimed: wanderClaimed
    };
  }

  /* Small util that read the signed-in email from the Your Hall token.
     Returns '' when not signed in. */
  function signedInEmail(){
    return (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
  }

  /* Token getter for PIN-required RPCs. We never expose the PIN value,
     just pass it directly into Supabase calls. */
  function readPin(){
    try {
      var raw = localStorage.getItem('hoh-your-hall-token');
      if(!raw) return '';
      var t = JSON.parse(raw);
      return (t && t.pin) ? t.pin : '';
    } catch(e){ return ''; }
  }

  /* ============================================================
     BATTLEGROUNDS TICKETS (R376, 2026-08-06)
     A second currency, separate from raffle tickets. One ticket is
     one run in the Battlegrounds. You get one free each day for
     finishing Explore the Hall, and the Shop sells them for 300
     hearts. The server wallet (hoh_bg_* RPCs) is the truth; this
     block keeps a local mirror so every surface can paint a number
     without a round trip. All writers dispatch hoh:bg-tickets-changed
     so the header pill, the Shop module and the Battlegrounds door
     repaint together.
     ============================================================ */
  var BG_KEY = 'hoh-bg-tickets-v1';
  function bgAccount(){ return String(signedInEmail() || 'guest').trim().toLowerCase() || 'guest'; }
  function bgScope(){ return { account:bgAccount(), email:signedInEmail(), pin:readPin() }; }
  function bgScopeCurrent(scope){ return !!scope && bgAccount()===scope.account && String(readPin()||'')===String(scope.pin||''); }
  function bgLocalSandbox(){
    try { if(typeof window.hohContractIsLocal === 'function' && window.hohContractIsLocal()) return true; } catch(e){}
    /* Contract data may still be loading in the anonymous god test harness.
       That is the only implicit fallback; a normal email-only session is a
       real account awaiting its PIN, never a local wallet. */
    try { return typeof window.hohIsGodMode === 'function' && window.hohIsGodMode() && !signedInEmail() && !readPin(); } catch(e){ return false; }
  }
  function bgStorageKey(account){ return BG_KEY + ':' + encodeURIComponent(String(account || bgAccount()).toLowerCase()); }
  function bgRead(account){
    try { var v = parseInt(localStorage.getItem(bgStorageKey(account)) || '0', 10); return (isNaN(v) || v < 0) ? 0 : v; }
    catch(e){ return 0; }
  }
  function bgWrite(n, account){
    n = Math.max(0, n | 0);
    account=String(account||bgAccount()).trim().toLowerCase()||'guest';
    try { localStorage.setItem(bgStorageKey(account), String(n)); } catch(e){}
    /* A late A response may safely refresh A's scoped mirror while B is on
       screen, but it must never repaint B with A's number. */
    if(account===bgAccount()){
      try { document.dispatchEvent(new CustomEvent('hoh:bg-tickets-changed', { detail: { balance: n, account:account } })); } catch(e){}
      if(typeof window.hohRenderHeaderCounter === 'function'){ try { window.hohRenderHeaderCounter(); } catch(e){} }
    }
    return n;
  }
  window.hohGetBgTickets = bgRead;
  window.hohSetBgTicketsLocal = bgWrite;
  function bgAccountBoundaryPaint(){
    window.__hohBgExploreClaimed = false;
    /* The door sync latch is credential-scoped. Clearing both the completed
       and in-flight keys lets Account B fetch its own server balance even
       when Account A already painted this page. */
    window.__hohBgSyncedAccount = '';
    window.__hohBgSyncPendingAccount = '';
    window.__hohBgSyncRetryAt = 0;
    var account=bgAccount(), balance=bgRead(account);
    try { document.dispatchEvent(new CustomEvent('hoh:bg-tickets-changed', { detail:{ balance:balance, account:account } })); }catch(e){}
    try { if(typeof window.hohRenderHeaderCounter==='function') window.hohRenderHeaderCounter(); }catch(e){}
  }
  try{
    document.addEventListener('hoh:signed-in',bgAccountBoundaryPaint);
    document.addEventListener('hoh:signed-out',bgAccountBoundaryPaint);
    window.addEventListener('storage',function(ev){ if(ev&&ev.key==='hoh-your-hall-token') bgAccountBoundaryPaint(); });
  }catch(e){}
  /* Pull the server balance into the mirror. Safe to call often. */
  function bgExploreDayKey(account){
    var d = new Date().toISOString().slice(0, 10);
    return 'hoh-bg-explore-' + d + ':' + encodeURIComponent(String(account||bgAccount()).toLowerCase());
  }
  function bgMarkExploreClaimed(account){
    account=String(account||bgAccount()).toLowerCase();
    if(account===bgAccount()) window.__hohBgExploreClaimed = true;
    try { localStorage.setItem(bgExploreDayKey(account), '1'); } catch(e){}
  }
  window.hohBgExploreClaimedToday = function(){
    try { return localStorage.getItem(bgExploreDayKey()) === '1'; } catch(e){ return false; }
  };
  window.hohSyncBgTickets = function(){
    return new Promise(function(resolve){
      var sb = window.hohSupabaseClient, scope=bgScope(), em = scope.email, pin = scope.pin;
      if(!em || !pin){ resolve({ ok:false, status:'sign_in_required', balance:bgRead(scope.account) }); return; }
      if(!sb){ resolve({ ok:false, status:'still_loading', balance:bgRead(scope.account) }); return; }
      sb.rpc('hoh_bg_state', { p_email: em, p_pin: pin }).then(function(res){
        var row = res && res.data && (Array.isArray(res.data) ? res.data[0] : res.data);
        if(res && !res.error && row && typeof row.balance === 'number'){
          if(row.explore_claimed_today) bgMarkExploreClaimed(scope.account);
          else {
            try{ localStorage.removeItem(bgExploreDayKey(scope.account)); }catch(e){}
            if(bgScopeCurrent(scope)) window.__hohBgExploreClaimed = false;
          }
          bgWrite(row.balance,scope.account);
          resolve({ ok:true, balance: row.balance, exploreClaimed: !!row.explore_claimed_today });
        } else { resolve({ ok:false, balance: bgRead(scope.account) }); }
      }).catch(function(){ resolve({ ok:false, balance: bgRead(scope.account) }); });
    });
  };
  /* The daily free ticket for finishing Explore. The server enforces
     once per day, so calling this twice is safe and quiet. */
  window.hohBgGrantExplore = function(){
    return new Promise(function(resolve){
      var sb = window.hohSupabaseClient, scope=bgScope(), em = scope.email, pin = scope.pin;
      if(!sb || !em || !pin){ resolve({ ok:false }); return; }
      sb.rpc('hoh_bg_grant_explore', { p_email: em, p_pin: pin }).then(function(res){
        var row = res && res.data && (Array.isArray(res.data) ? res.data[0] : res.data);
        if(res && !res.error && row){
          if(typeof row.balance === 'number') bgWrite(row.balance,scope.account);
          bgMarkExploreClaimed(scope.account);
          resolve({ ok:true, granted: !!row.granted, balance: row.balance });
        } else { resolve({ ok:false }); }
      }).catch(function(){ resolve({ ok:false }); });
    });
  };
  /* R400 (Jan): ONE FREE ENTRY A DAY AT THE DOOR, the daily pack's twin.
     Server enforced once per UTC day (hoh_bg_grant_daily, its own partial
     unique index), local day-marker so the door can paint the timer without
     a round trip. Idempotent: a second call answers granted false. God and
     local sandboxes get a localStorage-only grant so the flow stays
     testable signed out. */
  /* R401: THE FREE RUN COMES BACK EVERY 12 HOURS, and the window is ONE
     number that lives on the server. R401 moved the gate from a UTC date to
     floor(epoch / 43200) and hoh_bg_grant_daily now reports window_hours in
     its answer, so this file learns the real window the first time a player
     claims and never has to be redeployed to follow it. The constant below is
     only the value used before the server has ever answered.
     A label and a gate drift apart the moment there are two copies of the
     number. This is the one copy. */
  var BG_FREE_WINDOW_HOURS = 12;
  window.hohBgFreeWindowHours = function(){
    var h = 0;
    try { h = parseInt(localStorage.getItem('hoh-bg-free-window-h'), 10) || 0; } catch(e){}
    return (h > 0 && h <= 24) ? h : BG_FREE_WINDOW_HOURS;
  };
  function bgDailyDayKey(account){
    var w = window.hohBgFreeWindowHours() * 3600000;
    /* windows are counted from the epoch, so the boundary is the same instant
       for every player on earth and never depends on the device clock's zone */
    return 'hoh-bg-daily-w' + Math.floor(new Date().getTime() / w) + ':' + encodeURIComponent(String(account||bgAccount()).toLowerCase());
  }
  function bgMarkDailyClaimed(account){
    account=String(account||bgAccount()).toLowerCase();
    try { localStorage.setItem(bgDailyDayKey(account), '1'); } catch(e){}
    if(account===bgAccount()) try { document.dispatchEvent(new CustomEvent('hoh:bg-tickets-changed', { detail: { balance: bgRead(account), account:account } })); } catch(e){}
  }
  window.hohBgDailyClaimedToday = function(){
    try { return localStorage.getItem(bgDailyDayKey()) === '1'; } catch(e){ return false; }
  };
  window.hohClaimDailyBgEntry = function(){
    return new Promise(function(resolve){
      var sb = window.hohSupabaseClient, scope=bgScope(), em = scope.email, pin = scope.pin;
      if(!sb || !em || !pin){
        if(bgLocalSandbox()){
          /* Explicit anonymous god/sandbox path: once per window, local only. */
          if(window.hohBgDailyClaimedToday()){ resolve({ ok:true, granted:false, balance:bgRead(scope.account), sandbox:true }); return; }
          bgMarkDailyClaimed(scope.account);
          resolve({ ok:true, granted:true, balance:bgWrite(bgRead(scope.account)+1,scope.account), sandbox:true });
          return;
        }
        /* Email-only auto-sign-in is not authentication for a PIN-protected
           server wallet, and a slow client bootstrap is not a sandbox. Never
           mint a phantom ticket or burn the local daily marker in either case. */
        resolve({ ok:false, granted:false, status:(!em||!pin)?'sign_in_required':'still_loading', balance:bgRead(scope.account) });
        return;
      }
      sb.rpc('hoh_bg_grant_daily', { p_email: em, p_pin: pin }).then(function(res){
        var row = res && res.data && (Array.isArray(res.data) ? res.data[0] : res.data);
        if(res && !res.error && row){
          /* R401: take the window from the answer. If this migration has not
             been applied yet the field is simply absent and the default above
             stands, so an old server and a new theme still agree. */
          if(typeof row.window_hours === 'number' && row.window_hours > 0 && row.window_hours <= 24){
            try { localStorage.setItem('hoh-bg-free-window-h', String(row.window_hours)); } catch(e){}
          }
          if(typeof row.balance === 'number') bgWrite(row.balance,scope.account);
          bgMarkDailyClaimed(scope.account);
          resolve({ ok:true, granted: !!row.granted, balance: row.balance });
        } else { resolve({ ok:false, granted:false, balance: bgRead(scope.account) }); }
      }).catch(function(){ resolve({ ok:false, granted:false, balance: bgRead(scope.account) }); });
    });
  };
  /* R559: one paid entry is one immutable server request and one recoverable
     local Heart debit. The old two-argument RPC could commit and lose its
     response; both callers then refunded 300 Hearts and left the granted
     entry behind. A persisted request id is now reused after reload/network
     loss, and an ambiguous transport NEVER refunds or starts a second buy.
     The new server RPC stores the matching receipt. */
  var BG_BUY_PRICE = 300;
  var BG_BUY_PENDING_KEY = 'hoh-bg-buy-pending-v2';
  var HEART_WALLET_LOCK_NAME = 'hoh-heart-wallet-v2';
  var HEART_WALLET_LEASE_KEY = 'hoh-heart-wallet-lease-v2';
  function bgBuyJsonRead(key){
    try{
      var raw=localStorage.getItem(key); if(raw==null||raw==='')return null;
      var v=JSON.parse(raw); return v&&typeof v==='object'&&!Array.isArray(v)?v:null;
    }
    catch(e){ return null; }
  }
  function bgBuyJsonWrite(key,value){
    try{
      var raw=JSON.stringify(value||{}); localStorage.setItem(key,raw);
      return localStorage.getItem(key)===raw;
    }catch(e){ return false; }
  }
  function bgBuyPending(scope){
    return bgBuyJsonRead(BG_BUY_PENDING_KEY+':'+encodeURIComponent(String(scope&&scope.account||bgAccount()).toLowerCase()));
  }
  function bgBuyPendingSave(scope,row){
    var key=BG_BUY_PENDING_KEY+':'+encodeURIComponent(String(scope&&scope.account||bgAccount()).toLowerCase());
    try{
      if(!row){localStorage.removeItem(key);return localStorage.getItem(key)==null;}
      return bgBuyJsonWrite(key,row);
    }catch(e){return false;}
  }
  function bgBuyId(){
    try{if(window.crypto&&crypto.randomUUID)return 'bg-'+crypto.randomUUID();}catch(e){}
    return 'bg-'+Date.now().toString(36)+'-'+Math.random().toString(36).slice(2)+'-'+Math.random().toString(36).slice(2);
  }
  function bgHeartRead(){
    try{return Math.max(0,parseInt(localStorage.getItem('hoh-heart-bank-v1')||'0',10)||0);}catch(e){return 0;}
  }
  function bgHeartRaw(){try{return String(localStorage.getItem('hoh-heart-bank-v1')||'0');}catch(e){return '';}}
  function bgDebitMarker(row){return String(row.after)+'|hoh:bg-buy:'+encodeURIComponent(String(row.request_id||''));}
  function bgDebitMarked(row){return bgHeartRaw()===bgDebitMarker(row);}
  function bgHeartDebit(row){
    var before=bgHeartRead(),raw=bgHeartRaw(),after=Number(row.after),marker=bgDebitMarker(row);
    if(before!==Number(row.before)||raw!==String(row.before))throw new Error('heart balance changed');
    if(after<0||after!==before-BG_BUY_PRICE)throw new Error('invalid heart debit');
    /* The numeric prefix keeps every legacy parseInt reader compatible. The
       request marker is written atomically with the balance, closing the
       prepared-row crash gap between two separate localStorage keys. */
    try{if(!window.__hohHeartHint&&window.hohHeartHint)window.hohHeartHint('buy_bg_entry');}catch(e){}
    localStorage.setItem('hoh-heart-bank-v1',marker);
    if(bgHeartRaw()!==marker)throw new Error('heart bank unavailable');
    return after;
  }
  function bgHeartDebitNotify(){
    try{document.dispatchEvent(new CustomEvent('hoh:hearts-bumped',{detail:{spent:BG_BUY_PRICE}}));}catch(e){}
    try{if(typeof window.hohRenderHeaderCounter==='function')window.hohRenderHeaderCounter();}catch(e){}
  }
  function bgHeartNormalizeDebit(row){
    var marker=bgDebitMarker(row),raw=bgHeartRaw();
    if(raw===String(row.after))return true;
    if(raw!==marker)return false;
    try{localStorage.setItem('hoh-heart-bank-v1',String(row.after));return bgHeartRaw()===String(row.after);}catch(e){return false;}
  }
  function bgRefundMarker(row){return String(row.refund_after)+'|hoh:bg-refund:'+encodeURIComponent(String(row.request_id||''));}
  function bgRefundMarked(row){return bgHeartRaw()===bgRefundMarker(row);}
  function bgHeartRefund(row){
    var before=bgHeartRead(),raw=bgHeartRaw(),marker=bgRefundMarker(row);
    if(before!==Number(row.refund_before)||raw!==String(row.refund_before))throw new Error('heart balance changed');
    try{if(!window.__hohHeartHint&&window.hohHeartHint)window.hohHeartHint('refund_bg_entry');}catch(e){}
    localStorage.setItem('hoh-heart-bank-v1',marker);
    if(bgHeartRaw()!==marker)throw new Error('heart bank unavailable');
    return Number(row.refund_after);
  }
  function bgHeartRefundNotify(){
    try{document.dispatchEvent(new CustomEvent('hoh:hearts-bumped',{detail:{gained:BG_BUY_PRICE}}));}catch(e){}
    try{if(typeof window.hohRenderHeaderCounter==='function')window.hohRenderHeaderCounter();}catch(e){}
  }
  function bgHeartNormalizeRefund(row){
    var marker=bgRefundMarker(row),raw=bgHeartRaw();
    if(raw===String(row.refund_after))return true;
    if(raw!==marker)return false;
    try{localStorage.setItem('hoh-heart-bank-v1',String(row.refund_after));return bgHeartRaw()===String(row.refund_after);}catch(e){return false;}
  }
  function heartWalletError(code,message){var e=new Error(message);e.code=code;return e;}
  function localHeartWalletLock(work){
    try{if(navigator.locks&&typeof navigator.locks.request==='function')return navigator.locks.request(HEART_WALLET_LOCK_NAME,function(){return Promise.resolve().then(work);});}catch(e){}
    /* Web Locks and this verified lease deliberately use one GLOBAL wallet
       identity. Ticket and dust journals remain account-specific, but every
       new mutation of hoh-heart-bank-v1 must pass this shared boundary. */
    var token=bgBuyId(),tries=0,leaseKey=HEART_WALLET_LEASE_KEY;
    function acquire(){
      var now=Date.now(),cur=bgBuyJsonRead(leaseKey);
      if(!cur||Number(cur.until||0)<now){
        if(!bgBuyJsonWrite(leaseKey,{token:token,until:now+120000}))return Promise.reject(heartWalletError('heart_wallet_storage','The Heart wallet lock could not be saved.'));
      }
      return new Promise(function(resolve){setTimeout(resolve,35+Math.floor(Math.random()*25));}).then(function(){
        var mine=bgBuyJsonRead(leaseKey);
        if(mine&&mine.token===token)return Promise.resolve().then(work).then(function(v){release();return v;},function(e){release();throw e;});
        if(++tries<80)return new Promise(function(resolve){setTimeout(resolve,50);}).then(acquire);
        throw heartWalletError('heart_wallet_busy','Another Heart action is still finishing.');
      });
    }
    function release(){
      var mine=bgBuyJsonRead(leaseKey);
      try{if(mine&&mine.token===token)localStorage.removeItem(leaseKey);}catch(e){}
    }
    return acquire();
  }
  if(typeof window.hohWithHeartWalletLock!=='function')window.hohWithHeartWalletLock=localHeartWalletLock;
  function bgBuyWithLease(scope,work){
    return Promise.resolve().then(function(){return window.hohWithHeartWalletLock(work);}).catch(function(e){
      if(e&&e.code==='heart_wallet_busy')return {ok:false,status:'purchase_busy'};
      if(e&&e.code==='heart_wallet_storage')return {ok:false,status:'storage_unavailable'};
      throw e;
    });
  }
  function bgBuyRefund(scope,row,status){
    if(!bgScopeCurrent(scope)){
      row.state='refund_due'; row.status=status||'refused'; bgBuyPendingSave(scope,row);
      return {ok:false,status:'refund_waiting'};
    }
    status=status||row.status||'refused';
    if(row.state==='refunded'){
      bgHeartNormalizeRefund(row);
      bgBuyPendingSave(scope,null);
      return {ok:false,status:status,refunded:true};
    }
    if(row.state!=='refunding'){
      bgHeartNormalizeDebit(row);
      row.state='refunding';row.status=status;row.refund_before=bgHeartRead();row.refund_after=row.refund_before+BG_BUY_PRICE;
      if(bgHeartRaw()!==String(row.refund_before))return {ok:false,status:'refund_waiting'};
      if(!bgBuyPendingSave(scope,row))return {ok:false,status:'refund_waiting'};
    }
    var current=bgHeartRead();
    try{
      if(current===Number(row.refund_before)&&bgHeartRaw()===String(row.refund_before)){
        if(bgHeartRefund(row)!==Number(row.refund_after))throw new Error('refund not verified');
      }else if(!bgRefundMarked(row))throw new Error('refund balance changed');
    }catch(e){return {ok:false,status:'refund_waiting'};}
    row.state='refunded';
    if(!bgBuyPendingSave(scope,row))return {ok:false,status:'refund_waiting'};
    bgHeartNormalizeRefund(row);
    if(!bgBuyPendingSave(scope,null))return {ok:false,status:'refund_waiting'};
    bgHeartRefundNotify();
    return {ok:false,status:status,refunded:true};
  }
  window.hohBgPurchasePending=function(){return !!bgBuyPending(bgScope());};
  function bgBuyRpcMissing(error){
    var code=String(error&&error.code||'').toUpperCase();
    var text=[error&&error.message,error&&error.details,error&&error.hint].join(' ');
    if(code==='PGRST202')return /hoh_bg_buy_idempotent/i.test(text)&&/could not find|schema cache/i.test(text);
    return code==='42883'&&/function\s+(?:public\.)?hoh_bg_buy_idempotent\s*\([^\n]*\)\s+does not exist/i.test(String(error&&error.message||''));
  }
  window.hohBgBuy = function(){
    var scope=bgScope();
    if(!scope.email||!scope.pin)return Promise.resolve({ok:false,status:'sign_in_required'});
    if(!window.hohSupabaseClient)return Promise.resolve({ok:false,status:'still_loading'});
    return bgBuyWithLease(scope,function(){
      if(!bgScopeCurrent(scope))return {ok:false,status:'account_changed'};
      var row=bgBuyPending(scope);
      if(row&&(row.state==='refund_due'||row.state==='refunding'||row.state==='refunded'))return bgBuyRefund(scope,row,row.status);
      if(!row){
        var before=bgHeartRead();
        if(before<BG_BUY_PRICE)return {ok:false,status:'not_enough_hearts'};
        row={request_id:bgBuyId(),price:BG_BUY_PRICE,state:'prepared',before:before,after:before-BG_BUY_PRICE,attempts:0,at:Date.now()};
        if(!bgBuyPendingSave(scope,row))return {ok:false,status:'storage_unavailable'};
        try{if(bgHeartDebit(row)!==row.after)throw new Error('debit not verified');}
        catch(e){return {ok:false,status:'confirming_payment'};}
        row.state='debited';
        if(!bgBuyPendingSave(scope,row)){
          /* The durable row is still `prepared` and its exact debit marker is
             intact. Never roll it back without a durable refund state: the
             next recovery can prove and advance this same request. */
          return {ok:false,status:'confirming_payment'};
        }
        bgHeartNormalizeDebit(row);
        bgHeartDebitNotify();
      }else if(row.state==='prepared'){
        /* Crash recovery around the journal transition: exact plain `before`
           means this request may debit now; only its atomic wallet marker
           proves an earlier debit. Plain `after` is deliberately ambiguous. */
        var current=bgHeartRead();
        if(current===Number(row.before)&&bgHeartRaw()===String(row.before)){
          try{if(bgHeartDebit(row)!==Number(row.after))throw new Error('debit not verified');}
          catch(e){return {ok:false,status:'confirming_payment'};}
        }else if(!bgDebitMarked(row)){
          /* A plain numeric `after` may belong to another legitimate 300-
             Heart action after a crash. Only this request's in-wallet marker
             proves the prepared debit; equality alone must never mint entry. */
          return {ok:false,status:'confirming_payment'};
        }
        row.state='debited';
        if(!bgBuyPendingSave(scope,row)){
          return {ok:false,status:'confirming_payment'};
        }
        bgHeartNormalizeDebit(row);
        bgHeartDebitNotify();
      }
      if(row.state==='debited'){
        row.state='submitting';
        /* Zero means the durable intent exists but no RPC has been invoked.
           The counter is persisted again immediately before every attempt. */
        if(!Number.isFinite(Number(row.attempts)))row.attempts=0;
        if(!bgBuyPendingSave(scope,row))return {ok:false,status:'confirming_payment'};
      }
      /* A pre-counter journal in submitting/confirming is fail-closed as an
         already-attempted request. This matters during rollout and also makes
         it impossible to call a retry "virgin" after a crash. Persisting the
         increment before invoking PostgREST is the proof used below. */
      if(!Number.isFinite(Number(row.attempts)))row.attempts=1;
      var firstServerAttempt=row.state==='submitting'&&Number(row.attempts)===0;
      row.attempts=Math.max(0,Number(row.attempts)||0)+1;
      row.state='submitting';
      row.last_attempt_at=Date.now();
      if(!bgBuyPendingSave(scope,row))return {ok:false,status:'confirming_purchase',ambiguous:true};
      var client=window.hohSupabaseClient;
      return Promise.resolve(client.rpc('hoh_bg_buy_idempotent',{
        p_email:scope.email,p_pin:scope.pin,p_request_id:row.request_id
      })).then(function(res){
        if(res&&res.error){
          /* Gateway/timeout errors can arrive as resolved PostgREST objects
             after the database committed. Only a positively missing RPC is
             known not to have executed; every other error retries the same
             immutable request id. */
          if(bgBuyRpcMissing(res.error)&&firstServerAttempt)return bgBuyRefund(scope,row,'database_update_required');
          row.state='confirming';bgBuyPendingSave(scope,row);
          return {ok:false,status:'confirming_purchase',ambiguous:true};
        }
        var out=res&&res.data&&(Array.isArray(res.data)?res.data[0]:res.data);
        if(out&&out.ok===true&&typeof out.balance==='number'){
          bgWrite(out.balance,scope.account); bgBuyPendingSave(scope,null);
          if(!bgScopeCurrent(scope))return {ok:false,status:'account_changed',committed:true,balance:out.balance,request_id:row.request_id,replayed:!!out.replayed};
          return {ok:true,status:'ready',balance:out.balance,request_id:row.request_id,replayed:!!out.replayed};
        }
        if(out&&out.ok===false)return bgBuyRefund(scope,row,String(out.status||out.reason||'refused'));
        row.state='confirming';bgBuyPendingSave(scope,row);
        return {ok:false,status:'confirming_purchase',ambiguous:true};
      },function(){
        row.state='confirming';bgBuyPendingSave(scope,row);
        return {ok:false,status:'confirming_purchase',ambiguous:true};
      });
    });
  };
  /* Spend one at the moment a run actually starts. ok false at zero. */
  window.hohBgSpend = function(){
    return new Promise(function(resolve){
      var sb = window.hohSupabaseClient, scope=bgScope(), em = scope.email, pin = scope.pin;
      if(!sb || !em || !pin){ resolve({ ok:false, balance: bgRead(scope.account) }); return; }
      sb.rpc('hoh_bg_spend', { p_email: em, p_pin: pin }).then(function(res){
        var row = res && res.data && (Array.isArray(res.data) ? res.data[0] : res.data);
        if(res && !res.error && row){
          if(typeof row.balance === 'number') bgWrite(row.balance,scope.account);
          resolve({ ok: !!row.ok, balance: row.balance });
        } else { resolve({ ok:false, balance: bgRead(scope.account) }); }
      }).catch(function(){ resolve({ ok:false, balance: bgRead(scope.account) }); });
    });
  };
  /* Heal a spend whose run never started. The server only refunds when
     the recent ledger shows an unmatched spend, so this cannot mint. */
  window.hohBgRefund = function(){
    return new Promise(function(resolve){
      var sb = window.hohSupabaseClient, scope=bgScope(), em = scope.email, pin = scope.pin;
      if(!sb || !em || !pin){ resolve({ ok:false }); return; }
      sb.rpc('hoh_bg_refund', { p_email: em, p_pin: pin }).then(function(res){
        var row = res && res.data && (Array.isArray(res.data) ? res.data[0] : res.data);
        if(res && !res.error && row && typeof row.balance === 'number'){
          bgWrite(row.balance,scope.account);
          resolve({ ok:true, balance: row.balance });
        } else { resolve({ ok:false }); }
      }).catch(function(){ resolve({ ok:false }); });
    });
  };
  /* R560 · ABILITY TIER ODDS.
     Paid rerolls are adjacent-tier attempts, so there is no honest single
     percentage without the slot's current ability rarity. Tickets multiply
     that tier's base chance by 2% RELATIVE each (50-ticket cap); they never add
     flat points that would turn Mythic into an ordinary roll. */
  window.hohAbilityTierPct = function(rarity, tickets, isFF){
    var r = Math.max(0, Math.min(5, rarity | 0));
    var base = r === 0 ? 12 : r === 1 ? 6 : r === 2 ? 1.8 : r === 3 ? 0.3 : (r === 4 && isFF ? 0.05 : 0);
    var mult = 1 + Math.min(50, Math.max(0, tickets | 0)) * 0.02;
    return Math.round(base * mult * 1000) / 1000;
  };
  /* Compatibility for a cached surface that has not learned the slot floor.
     Rare → Epic is the named reference step; current UI uses the helper above. */
  window.hohPotentialPct = function(tickets){
    return window.hohAbilityTierPct(2, tickets, true);
  };

  /* R136-fix-4: Server-side daily-claim gate. Calls the
     claim_daily_milestone(email, pin, key) RPC defined in
     sql/R136-server-daily-claims.sql. The RPC atomically inserts a
     row keyed by (email, day, milestone) and returns whether THIS
     call wrote it. Cross-device implication: if the user already
     claimed `wander_20` on desktop, mobile's call returns
     {already:true} and we skip the local credit + show a toast.
     Returns a Promise that resolves to:
       { ok: true,  newClaim: true  } — claim landed, do the credit
       { ok: true,  newClaim: false } — already claimed today; skip
       { ok: false, reason: '...'   } — RPC missing / not signed in /
                                         network error → fall back to
                                         local-only path so the user
                                         isn't blocked when the SQL
                                         migration hasn't been applied
                                         yet (graceful degradation).
     If the SQL hasn't been applied, the RPC returns a 404 from
     PostgREST; we treat that as ok:false and let the local path
     proceed — same behavior as before this fix. */
  window.hohServerClaimDailyMilestone = function(milestoneKey){
    return new Promise(function(resolve){
      var em = signedInEmail();
      var pin = readPin();
      var sb = window.hohSupabaseClient;
      /* Anonymous (signed-out) users can't be deduped server-side —
         no auth identity. Fall through to the local path. The cross-
         device exploit requires being signed in on both devices, so
         this branch is the legitimate-user case. */
      if(!em || !pin || !sb || typeof sb.rpc !== 'function'){
        resolve({ ok: false, reason: 'unauthenticated' });
        return;
      }
      try {
        sb.rpc('claim_daily_milestone', {
          user_email: em,
          user_pin: pin,
          milestone_key: milestoneKey
        }).then(function(res){
          if(res && res.error){
            /* PostgREST 404 means the RPC doesn't exist (SQL not
               applied yet). Don't block the user — degrade to local.
               Other errors (auth_failed, missing_param) also degrade
               so a transient hiccup doesn't strand a legitimate
               claim. */
            try { console.warn('[ServerDailyClaim] RPC error', res.error); } catch(_){}
            resolve({ ok: false, reason: 'rpc_error', error: res.error });
            return;
          }
          var data = (res && res.data) || {};
          if(data.claimed === true){
            resolve({ ok: true, newClaim: true });
          } else if(data.already === true){
            resolve({ ok: true, newClaim: false });
          } else {
            resolve({ ok: false, reason: 'unexpected_response' });
          }
        }, function(err){
          try { console.warn('[ServerDailyClaim] RPC threw', err); } catch(_){}
          resolve({ ok: false, reason: 'rpc_throw' });
        });
      } catch(_){
        resolve({ ok: false, reason: 'rpc_sync_throw' });
      }
    });
  };

  /* R141-C: Server → client daily-claims sync. Called on page load
     (and after fetchRaffleState) for signed-in users. The server is
     the authoritative truth for which milestones the ACCOUNT has
     claimed today. We pull that list and pre-mark the matching
     localStorage flags so a user opening the site on a fresh device
     immediately sees the correct quest state.
     Why this exists: the cross-device farming exploit was that
     wander_20 and wander_40 are CLIENT-only flags. A user could
     log in on device 2, see wander progress at 0/2, do the quests
     again, earn 2 more wallet tickets, apply them, and the server
     accepted the apply because the ticket-grant gate wasn't
     account-aware. The R136 server gate is supposed to refuse the
     second device's CLAIM, but only if the milestone is already
     marked claimed locally too. This function does that pre-mark.
     The localStorage key shape mirrors the one in hoh-fresh-faces.js
     (`hoh-ff-claim-{ymd}-{n}`) so isMilestoneClaimed reads "claimed"
     the moment we set it, which causes the quest UI to render
     greyed out and refuses any tap-to-claim attempt. */
  /* R406 BUGFIX, pre-existing and load-bearing. This built the day
     string from LOCAL time while hoh-fresh-faces.js keys the very same
     flag off UTC (ymdToday is toISOString().slice(0,10)). For anyone
     whose local date differs from the UTC date — which is most of
     Europe every evening and most of the Americas every night — the
     cross-device pre-mark wrote a key the quest bar never reads. The
     whole point of this sync is to stop the same account claiming a
     milestone twice by switching devices, and for a slice of every day
     it silently did nothing. Now it agrees with the reader.
     Note the ledger keys elsewhere in this file (todayKey) stay local:
     that namespace is written and read only here, so it is already
     self-consistent, and moving it would orphan today's ledgers. */
  function _claimsLocalKey(n){
    try {
      var ymd = new Date().toISOString().slice(0,10);
      return 'hoh-ff-claim-' + ymd + '-' + n;
    } catch(_){ return ''; }
  }
  window.hohServerSyncDailyClaims = function(){
    var em = signedInEmail();
    var pin = readPin();
    var sb = window.hohSupabaseClient;
    if(!em || !pin || !sb || typeof sb.rpc !== 'function') return Promise.resolve(null);
    return new Promise(function(resolve){
      try {
        sb.rpc('get_my_daily_claims', {
          user_email: em,
          user_pin: pin
        }).then(function(res){
          if(res && res.error){
            try { console.warn('[DailyClaims] sync RPC error', res.error); } catch(_){}
            resolve(null);
            return;
          }
          var data = (res && res.data) || {};
          var list = Array.isArray(data.milestones) ? data.milestones : [];
          /* Walk the server's claim list. For each known milestone
             slot, write the matching localStorage flag so the local
             UI treats it as already-claimed. Unknown slots are
             ignored — server may add new ones in future without
             breaking older clients. */
          var marked = [];
          list.forEach(function(slot){
            /* Ticket slots are wander_20 / wander_40 / wander_60;
               heart-bonus slots are wander_10 / wander_30 / wander_50.
               R406 moved the heart THRESHOLDS behind these names (the
               ladder is 0/10/20/30/40/50 now) but deliberately left the
               slot strings alone, so everything in this file still
               joins correctly and needs no migration. Same-day device
               switches mark the local flag so the bar paints the
               correct claimed state. */
            if(slot === 'wander_20'){
              try { localStorage.setItem(_claimsLocalKey(20), '1'); marked.push('wander_20'); } catch(_){}
            } else if(slot === 'wander_40'){
              try { localStorage.setItem(_claimsLocalKey(40), '1'); marked.push('wander_40'); } catch(_){}
            } else if(slot === 'wander_60'){
              try { localStorage.setItem(_claimsLocalKey(60), '1'); marked.push('wander_60'); } catch(_){}
            } else if(slot === 'wander_10' || slot === 'wander_30' || slot === 'wander_50'){
              /* Legacy ticket slot names from the prior schema. The
                 pips at 10/30/50 are now heart-bonuses (no ticket
                 credit) but we still mark the local flag so the pip
                 renders claimed and the user doesn't try to claim it
                 again. */
              var legacyN = parseInt(slot.split('_')[1], 10);
              if(!isNaN(legacyN)){
                try { localStorage.setItem(_claimsLocalKey(legacyN), '1'); marked.push(slot); } catch(_){}
              }
            }
            /* Add new server-side daily slots here when they get coverage. */
          });
          /* If we marked anything, repaint the quest progress so the
             user immediately sees the correct claimed state without
             needing to tap a milestone. */
          if(marked.length){
            try {
              if(typeof window.hohPaintQuestProgress === 'function'){
                window.hohPaintQuestProgress();
              }
              /* Fire a state event so any other surface that cares
                 (header pill, daily tickets modal) can repaint with
                 the up-to-date "claimed today" count. */
              document.dispatchEvent(new CustomEvent('hoh:ticket-state-changed', {
                detail: { source: 'serverDailySync', marked: marked }
              }));
            } catch(_){}
          }
          resolve({ ok: true, marked: marked });
        }, function(err){
          try { console.warn('[DailyClaims] sync RPC threw', err); } catch(_){}
          resolve(null);
        });
      } catch(_){
        resolve(null);
      }
    });
  };

  /* R64: Central apply-ticket entry point. The user spends one
     unspent ticket on a character they own. Steps:
       1. Validate: wallet > 0, hero exists.
       2. Optimistic local update: decrement wallet, bump c.tickets
          on every cached copy of the character (my_characters[],
          gallery pool).
       3. Fire animation from header pill → target element.
       4. Live-update any open breakdown lightbox totals + win %.
       5. Call server RPC apply_pending_ticket(character_id) to
          persist. On error, refund the wallet + revert local
          c.tickets and toast.
     opts.targetEl — the DOM element to fly the ticket toward.
     opts.silent — skip the toast (used by the +1 chip in the card
     when we already show a per-card visual).
     Returns true if the apply was kicked off, false otherwise. */
  function ticketCharacterForId(characterId){
    var pools = [];
    try { pools.push((window.hohTicketState && window.hohTicketState.my_characters) || []); } catch(_){}
    try { if(typeof window.hohShopOwnedCards === 'function') pools.push(window.hohShopOwnedCards() || []); } catch(_){}
    try { if(typeof window.hohGetAllCharacters === 'function') pools.push(window.hohGetAllCharacters() || []); } catch(_){}
    for(var p = 0; p < pools.length; p++){
      for(var i = 0; i < pools[p].length; i++){
        if(pools[p][i] && String(pools[p][i].id) === String(characterId)) return pools[p][i];
      }
    }
    return null;
  }

  function ticketOwnsActiveCard(characterId){
    try {
      if(typeof window.hohShopOwnedCards === 'function'){
        var collected = window.hohShopOwnedCards() || [];
        for(var i = 0; i < collected.length; i++){
          if(collected[i] && String(collected[i].id) === String(characterId)) return true;
        }
      }
    } catch(_){}
    /* Startup fallback before the collection cache is available. Submitted
       heroes are granted their card automatically; the server still enforces
       the active-copy rule before committing anything. */
    try {
      var submitted = (window.hohTicketState && window.hohTicketState.my_characters) || [];
      for(var j = 0; j < submitted.length; j++){
        if(submitted[j] && String(submitted[j].id) === String(characterId)) return true;
      }
    } catch(_){}
    return false;
  }
  /* One shared presentation gate for every Hall surface. The server still
     rechecks the active collected copy and separately decides whether the
     recipient was submitted by this account and may gain a level. */
  window.hohCanApplyTicketToCharacter = ticketOwnsActiveCard;

  /* ============ R695 · THE HALL ASKS IN ITS OWN VOICE ============
     Jan: "this notification is so ghetto and shitty ... make sure the pop up
     is not a fucking website warning and actually a well designed minimal
     message."

     `window.confirm` renders the BROWSER's chrome, so it opened with
     "framedfantasy.com says" above Jan's copy in a system font on a system
     panel — the visual language of a security warning, attached to a small
     act of generosity. No amount of rewriting the sentence fixes that,
     because the frame is the problem.

     This is deliberately NOT a modal framework. It is one function, one
     panel, the Hall's own palette and its two real faces (Cinzel for the
     label, Cormorant Garamond for the sentence). It resolves a promise, so a
     caller reads exactly like the confirm() it replaces.

     Defined here because hoh-tickets.js is loaded BEFORE hoh-your-hall.js and
     hoh-shop.js in sections/hall-of-heroes.liquid (both deferred, so order
     holds), which lets the other ticket surfaces share this one panel instead
     of each growing its own. Every caller still falls back to confirm() if
     this is somehow absent, so a load-order change degrades rather than
     breaks. */
  var HOH_ASK_CSS_ID = 'hohAskStyle';
  function hohAskEnsureCss(){
    if(document.getElementById(HOH_ASK_CSS_ID)) return;
    var st = document.createElement('style');
    st.id = HOH_ASK_CSS_ID;
    st.textContent =
      '.hoh-ask{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;' +
        'justify-content:center;padding:24px;background:rgba(6,5,10,.74);' +
        '-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);opacity:0;' +
        'transition:opacity .16s ease;}' +
      '.hoh-ask.is-in{opacity:1;}' +
      /* the panel. One hairline, one radius, no glow: the point is that it
         reads as part of the Hall and not as an alert. */
      '.hoh-ask-panel{width:min(400px,100%);box-sizing:border-box;background:#12101a;' +
        'border:1px solid rgba(232,198,116,.20);border-radius:14px;' +
        'box-shadow:0 24px 60px -22px rgba(0,0,0,.85);' +
        'padding:26px 26px 20px;transform:translateY(6px) scale(.985);' +
        'transition:transform .18s cubic-bezier(.2,.7,.3,1);}' +
      '.hoh-ask.is-in .hoh-ask-panel{transform:none;}' +
      '.hoh-ask-title{margin:0 0 14px;font-family:\'Cinzel\',serif;font-size:11.5px;' +
        'font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:#e8c674;}' +
      '.hoh-ask-give-ticket .hoh-ask-title{text-align:center;}' +
      '.hoh-ask-body{margin:0;font-family:\'Cormorant Garamond\',Georgia,serif;' +
        'font-size:16px;line-height:1.6;color:#cfc7b8;}' +
      '.hoh-ask-body b{color:#e8e0d0;font-weight:600;}' +
      /* the caveat sits quieter than the question, because it is a caveat */
      '.hoh-ask-note{margin:12px 0 0;font-family:\'Cormorant Garamond\',Georgia,serif;' +
        'font-size:14px;line-height:1.55;color:#9a92a5;}' +
      '.hoh-ask-actions{display:flex;align-items:center;justify-content:flex-end;gap:8px;' +
        'margin:22px -6px -6px 0;}' +
      /* 44px tap targets on both, per the Hall's mobile rule */
      '.hoh-ask-btn{-webkit-appearance:none;appearance:none;cursor:pointer;' +
        'min-height:44px;padding:0 18px;border-radius:999px;background:transparent;' +
        'font-family:\'Cinzel\',serif;font-size:10.5px;font-weight:600;letter-spacing:.14em;' +
        'text-transform:uppercase;transition:color .16s ease,border-color .16s ease,' +
        'background .16s ease;}' +
      '.hoh-ask-btn:focus-visible{outline:2px solid rgba(232,198,116,.65);outline-offset:2px;}' +
      '.hoh-ask-cancel{border:1px solid transparent;color:#9a92a5;}' +
      '.hoh-ask-cancel:hover{color:#cfc7b8;}' +
      '.hoh-ask-ok{border:1px solid rgba(232,198,116,.55);color:#e8c674;}' +
      '.hoh-ask-ok:hover{background:rgba(232,198,116,.10);border-color:rgba(232,198,116,.85);}' +
      /* R734 (Jan): the ticket panel is one centred composition, title to
         buttons. The generic ask keeps its right-aligned actions. */
      '.hoh-ask-give-ticket .hoh-ask-body,.hoh-ask-give-ticket .hoh-ask-note{text-align:center;}' +
      '.hoh-ask-give-ticket .hoh-ask-actions{justify-content:center;margin-right:0;}' +
      /* R734: the warning variant. Shown only when the ticket is going to a
         hero the player did not submit. Red carries the one fact that
         matters: this ticket does not count toward their own raffle. */
      '.hoh-ask-warn{border-color:rgba(224,92,92,.38);}' +
      '.hoh-ask-warn .hoh-ask-note{margin:14px 0 0;padding:11px 14px;border-radius:10px;' +
        'background:rgba(224,92,92,.09);border:1px solid rgba(224,92,92,.26);color:#e0a2a2;}' +
      '.hoh-ask-warn .hoh-ask-note b{color:#f2b6b6;font-weight:600;}' +
      '.hoh-ask-warn .hoh-ask-ok{border-color:rgba(224,92,92,.6);color:#e89a9a;}' +
      '.hoh-ask-warn .hoh-ask-ok:hover{background:rgba(224,92,92,.10);border-color:rgba(224,92,92,.85);}' +
      '.hoh-ask-warn .hoh-ask-ok:focus-visible{outline-color:rgba(224,92,92,.65);}' +
      '@media (max-width:420px){' +
        '.hoh-ask{padding:16px;}' +
        '.hoh-ask-panel{padding:22px 20px 16px;}' +
        '.hoh-ask-actions{flex-direction:column-reverse;align-items:stretch;gap:6px;margin-right:0;}' +
        '.hoh-ask-btn{width:100%;}}' +
      '@media (prefers-reduced-motion:reduce){' +
        '.hoh-ask,.hoh-ask-panel{transition:none!important;}}';
    (document.head || document.documentElement).appendChild(st);
  }

  /* opts: { title, body, note, confirmLabel, cancelLabel } → Promise<boolean> */
  window.hohAsk = function(opts){
    opts = opts || {};
    return new Promise(function(resolve){
      var settled = false;
      function done(v){
        if(settled) return; settled = true;
        try { document.removeEventListener('keydown', onKey, true); } catch(_){}
        try { wrap.classList.remove('is-in'); } catch(_){}
        window.setTimeout(function(){ try { wrap.remove(); } catch(_){} }, 180);
        try { if(lastFocus && lastFocus.focus) lastFocus.focus(); } catch(_){}
        resolve(!!v);
      }
      var lastFocus = null;
      try { lastFocus = document.activeElement; } catch(_){}
      hohAskEnsureCss();
      var wrap = document.createElement('div');
      wrap.className = 'hoh-ask';
      wrap.setAttribute('role', 'dialog');
      wrap.setAttribute('aria-modal', 'true');
      var panel = document.createElement('div');
      panel.className = 'hoh-ask-panel' + (opts.panelClass === 'hoh-ask-give-ticket' ? ' hoh-ask-give-ticket' : '') + (opts.warn ? ' hoh-ask-warn' : '');
      function esc(s){
        return String(s == null ? '' : s)
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      }
      var titleId = 'hohAskT' + Date.now();
      panel.innerHTML =
        '<p class="hoh-ask-title" id="' + titleId + '">' + esc(opts.title || 'Confirm') + '</p>' +
        '<p class="hoh-ask-body">' + (opts.bodyHtml || esc(opts.body || '')) + '</p>' +
        (opts.noteHtml ? '<p class="hoh-ask-note">' + opts.noteHtml + '</p>' : (opts.note ? '<p class="hoh-ask-note">' + esc(opts.note) + '</p>' : '')) +
        '<div class="hoh-ask-actions">' +
          '<button type="button" class="hoh-ask-btn hoh-ask-cancel">' +
            esc(opts.cancelLabel || 'Cancel') + '</button>' +
          '<button type="button" class="hoh-ask-btn hoh-ask-ok">' +
            esc(opts.confirmLabel || 'Confirm') + '</button>' +
        '</div>';
      wrap.setAttribute('aria-labelledby', titleId);
      wrap.appendChild(panel);
      /* the backdrop cancels, the panel never does */
      wrap.addEventListener('click', function(e){ if(e.target === wrap) done(false); });
      panel.querySelector('.hoh-ask-cancel').addEventListener('click', function(){ done(false); });
      panel.querySelector('.hoh-ask-ok').addEventListener('click', function(){ done(true); });
      function onKey(e){
        if(e.key === 'Escape'){ e.preventDefault(); done(false); return; }
        /* a two-button dialog is its own focus trap */
        if(e.key === 'Tab'){
          var f = panel.querySelectorAll('.hoh-ask-btn');
          if(!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if(e.shiftKey && document.activeElement === first){ e.preventDefault(); last.focus(); }
          else if(!e.shiftKey && document.activeElement === last){ e.preventDefault(); first.focus(); }
        }
      }
      document.addEventListener('keydown', onKey, true);
      (document.body || document.documentElement).appendChild(wrap);
      try { panel.querySelector('.hoh-ask-ok').focus(); } catch(_){}
      window.requestAnimationFrame(function(){ wrap.classList.add('is-in'); });
    });
  };

  /* R734 (Jan): did the signed-in player submit this hero? Three sources,
     any one is enough: the roster's owner helper (email match), the account
     key the Hall uses everywhere, and the player's own submitted rows.
     Returns true, false, or null when nobody is signed in. */
  window.hohTicketTargetIsMine = function(characterId, c){
    var me = '';
    try { me = (typeof window.hohYourHallEmail === 'function') ? String(window.hohYourHallEmail() || '').trim().toLowerCase() : ''; } catch(_){}
    if(!me) return null;
    try { if(c && typeof window.hohIsCharacterOwner === 'function' && window.hohIsCharacterOwner(c)) return true; } catch(_){}
    try { if(c && typeof window.hohAccountKey === 'function' && window.hohAccountKey(c) === me) return true; } catch(_){}
    try {
      var rows = (typeof window.hohYourHallSubmittedRows === 'function') ? (window.hohYourHallSubmittedRows() || []) : [];
      for(var i = 0; i < rows.length; i++){
        if(rows[i] && String(rows[i].id) === String(characterId)) return true;
      }
    } catch(_){}
    return false;
  };

  /* The one place the ticket question is written, so the two surfaces that
     ask it cannot drift apart.
     R734 (Jan): the question is only asked for a hero the player did NOT
     submit, and then it is a warning. A ticket on their own hero applies
     without a prompt (info.mine === true resolves yes at once). When
     ownership is unknown (no info passed) the old neutral panel still shows,
     so a caller that forgets the flag degrades to the previous behaviour. */
  window.hohAskGiveTicket = function(heroName, info){
    var name = String(heroName || 'this hero').trim() || 'this hero';
    var mine = info && typeof info.mine === 'boolean' ? info.mine : null;
    function esc(s){
      return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    if(mine === true) return Promise.resolve(true);
    if(typeof window.hohAsk !== 'function'){
      try { return Promise.resolve(window.confirm('Give 1 raffle ticket to ' + name + '?')); }
      catch(_){ return Promise.resolve(false); }
    }
    if(mine === false){
      /* Jan's rule (2026-09-10): a ticket on a hero you did not submit only
         raises that card's Crystal tier-up odds. It adds NO raffle entry.
         Tickets on your own submitted heroes do both, which is the incentive.
         ⚠ Server gap at the time of writing: apply_pending_ticket (R687B)
         still adds every ticket to characters.tickets, which is also the
         raffle weight. The split needs its own SQL release. */
      return window.hohAsk({
        title: 'Give a ticket',
        bodyHtml: 'Give one raffle ticket to <b>' + esc(name) + '</b>?',
        noteHtml: 'You didn\'t submit ' + esc(name) + '. This ticket raises their Crystal tier-up odds and <b>does not add a raffle entry</b>. Tickets on heroes you submitted do both.',
        panelClass: 'hoh-ask-give-ticket',
        warn: true,
        confirmLabel: 'Give ticket',
        cancelLabel: 'Cancel'
      });
    }
    return window.hohAsk({
      title: 'Give a ticket',
      bodyHtml: 'Give one raffle ticket to <b>' + esc(name) + '</b>?',
      note: 'Give tickets to your own characters to improve your monthly raffle chances. This ticket also improves this character\'s Crystal tier-up odds.',
      panelClass: 'hoh-ask-give-ticket',
      confirmLabel: 'Give ticket',
      cancelLabel: 'Cancel'
    });
  };

  window.hohApplyTicket = function(characterId, opts){
    opts = opts || {};
    if(!characterId) return false;
    /* R793: the wallet apply (apply_pending_ticket) is the legacy path. In the
       live Hall a ticket is given only from the hero's own page, by
       hohHallGiveTicket below, so any old caller that is still wired gets a
       plain no. */
    if(r793Live()) return false;
    var have = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
    if(have <= 0){
      try { hohShowHallToast && hohShowHallToast('No tickets to apply yet. Earn more first.'); } catch(_){}
      return false;
    }
    /* R137: PRE-FLIGHT auth check. The apply RPC requires email + PIN.
       If the user isn't signed in, the optimistic +1 will get rolled
       back the moment we try to call the server — wasted motion that
       confuses the user. Refuse upfront with a clear message and
       leave the wallet ticket untouched so they can apply after
       signing in. */
    var preEm  = signedInEmail();
    var prePin = readPin();
    if(!preEm || !prePin){
      try { hohShowHallToast && hohShowHallToast('Sign in to apply tickets to your heroes.'); } catch(_){}
      return false;
    }
    if(!ticketOwnsActiveCard(characterId)){
      try { hohShowHallToast && hohShowHallToast('Collect this hero before giving them a ticket.'); } catch(_){}
      return false;
    }
    if(!opts.confirmed){
      var ticketHero = ticketCharacterForId(characterId);
      var ticketHeroName = String((ticketHero && (ticketHero.name || ticketHero.title)) || 'this hero').trim() || 'this hero';
      /* ⛔ R695 · THE ASK IS NOW ASYNCHRONOUS, AND `opts.confirmed` IS WHY THAT
         IS SAFE. window.confirm blocked the thread and returned a boolean, so
         this function could decide inline. A designed panel cannot block, so
         the call re-enters through the flag this branch already checked for —
         the resume path is the one that was always here, not a new one. The
         `return false` below is "not applied yet", exactly as it was for a
         declined confirm; every caller already treats it that way. */
      var resumeOpts = {};
      for(var ok2 in opts){ if(Object.prototype.hasOwnProperty.call(opts, ok2)) resumeOpts[ok2] = opts[ok2]; }
      resumeOpts.confirmed = true;
      try {
        /* R734: own hero, no prompt; someone else's hero, the red warning. */
        var ticketMine = (typeof window.hohTicketTargetIsMine === 'function') ? window.hohTicketTargetIsMine(characterId, ticketHero) : null;
        resumeOpts.mine = ticketMine;
        window.hohAskGiveTicket(ticketHeroName, { mine: ticketMine }).then(function(yes){
          if(yes) window.hohApplyTicket(characterId, resumeOpts);
        });
      } catch(_){
        /* last-resort fallback: never strand the action if the panel fails */
        var fb = false;
        try { fb = window.confirm('Give 1 raffle ticket to ' + ticketHeroName + '?'); } catch(_e){ fb = false; }
        if(fb) window.hohApplyTicket(characterId, resumeOpts);
      }
      return false;
    }
    /* Decrement wallet optimistically. */
    if(typeof window.hohSpendUnspentTicket === 'function'){
      var ok = window.hohSpendUnspentTicket();
      if(!ok) return false;
    }
    /* Bump c.tickets on every cached copy. Three caches to update:
       hohTicketState.my_characters[], the global getAllCharacters
       pool, and the "your hall submitted rows" if exposed. */
    function bumpCharacterTickets(id, delta, field){
      field = field || 'tickets';
      try {
        if(window.hohTicketState && Array.isArray(window.hohTicketState.my_characters)){
          window.hohTicketState.my_characters.forEach(function(r){
            if(r && String(r.id) === String(id)) r[field] = Math.max(0, (r[field] || 0) + delta);
          });
        }
      } catch(_){}
      try {
        if(typeof window.hohGetAllCharacters === 'function'){
          var pool = window.hohGetAllCharacters() || [];
          for(var i = 0; i < pool.length; i++){
            if(pool[i] && String(pool[i].id) === String(id)){
              pool[i][field] = Math.max(0, (pool[i][field] || 0) + delta);
              break;
            }
          }
        }
      } catch(_){}
    }
    /* R735 (Jan's rule): a ticket on a hero you did not submit raises only
       that card's tier-up odds, so locally it lands in guest_tickets and
       never in the raffle count. Unknown ownership keeps the old path. The
       server (R735B apply_pending_ticket) makes the same split. */
    var applyMine = (typeof opts.mine === 'boolean') ? opts.mine
      : ((typeof window.hohTicketTargetIsMine === 'function') ? window.hohTicketTargetIsMine(characterId, ticketCharacterForId(characterId)) : null);
    opts.mine = applyMine;
    var tkField = (applyMine === false) ? 'guest_tickets' : 'tickets';
    bumpCharacterTickets(characterId, 1, tkField);
    /* Visual: ticket fly from header pill to the target element. */
    try { flyTicketFromPillTo(opts.targetEl); } catch(_){}
    /* R66: Mythic blessing glow on the receiving surface — the card
       in My Submitted, the open detail modal, both. The "blessed"
       moment: rainbow border pulses, a brief brightness lift, then
       settles back. Treat as the most important visual feedback in
       the entire ticket loop — the user is making their hero more
       likely to win real raffle prizes. */
    try {
      var glowTargets = [];
      /* The card the click came from (Your Hall My Submitted). */
      if(opts.targetEl && opts.targetEl.closest){
        var card = opts.targetEl.closest('.hoh-yh-card');
        if(card) glowTargets.push(card);
      }
      /* If a detail modal is currently open on this character, glow
         the modal frame too so the user sees it from inside detail. */
      var modal = document.getElementById('hoh-modal');
      if(modal && modal.style.display !== 'none'){
        var modalContent = modal.querySelector('.hoh-modal-content');
        if(modalContent) glowTargets.push(modalContent);
      }
      glowTargets.forEach(function(el){
        try {
          el.classList.remove('hoh-mythic-bless-glow');
          void el.offsetWidth;
          el.classList.add('hoh-mythic-bless-glow');
          setTimeout(function(){ try { el.classList.remove('hoh-mythic-bless-glow'); } catch(_){} }, 1700);
        } catch(_){}
      });
    } catch(_){}
    /* Live-update the open breakdown lightbox if the user is staring
       at it (the most common case — they hover, see the count, click
       the pill). Re-runs hohBuildTicketBreakdownHtml against the
       just-bumped character so totals + win % refresh in place. */
    try { liveUpdateBreakdown(characterId); } catch(_){}
    /* Re-render the sticky pill so the wallet badge updates. */
    try { renderHeaderCounter(); } catch(_){}
    /* R136-fix-5: also bump the visible count chip on the detail
       modal pill (#hoh-detail-tk-count) when it exists for THIS
       character. Without this, applies fired from outside the modal
       (e.g. the Apply Ticket button on a card) would update the
       breakdown body but leave the outer chip stale until the user
       reopens the modal. */
    try {
      /* R735: the pill is the raffle count, so a guest ticket leaves it alone. */
      var detailPill = document.getElementById('hoh-detail-tk-pill');
      if(tkField === 'tickets' && detailPill && String(detailPill.getAttribute('data-character-id')) === String(characterId)){
        var detailCount = document.getElementById('hoh-detail-tk-count');
        if(detailCount){
          var dn = parseInt(detailCount.textContent || '0', 10) || 0;
          detailCount.textContent = String(dn + 1);
        }
      }
    } catch(_){}
    /* R137: success toast moved to the post-server-confirm path
       below. Showing "+1 applied" before the server confirms was the
       old lie — the user celebrated, but if the server failed they'd
       see a contradicting "Ticket not saved" toast 200ms later. Now
       the toast only fires after the apply is real. */
    /* Fire a state event so other surfaces (My Submitted card list,
       Daily Tickets modal) can repaint. */
    try {
      document.dispatchEvent(new CustomEvent('hoh:ticket-applied', {
        detail: { characterId: characterId }
      }));
    } catch(_){}
    /* R136-fix-5: deferred safety repaint. Some surfaces re-render
       their tickets section asynchronously (raffle state fetch,
       ticket-applied event handler chain, etc.) and could race the
       optimistic bump above. Fire one more renderHeaderCounter +
       liveUpdateBreakdown one frame later so any surface that just
       repainted with stale state catches up. Idempotent — calling
       these twice in quick succession just paints the same value
       twice. */
    try {
      requestAnimationFrame(function(){
        try { renderHeaderCounter(); } catch(_){}
        try { liveUpdateBreakdown(characterId); } catch(_){}
      });
    } catch(_){}
    /* R137: Server RPC. Now we ACTUALLY trust the response and roll
       back on failure. The previous version silently swallowed errors
       which meant when apply_pending_ticket didn't exist on the
       server (it didn't!), the user saw +1 in the UI but the server
       value never moved. On refresh the cache reloaded the stale
       server value and the +1 vanished — wallet ticket spent for
       nothing. The fix: wait for the server response, sync the local
       cache to the AUTHORITATIVE new_tickets count on success, and
       on ANY failure refund the wallet + revert the visual + toast
       the user honestly so they know to retry.
       client_request_id makes the call idempotent — if the network
       drops between the server commit and the client receiving the
       reply, the client retry sends the same request_id and the
       server returns 'already' with the cached new_tickets count
       instead of double-bumping. */
    try {
      var sb = window.hohSupabaseClient || (typeof supa === 'function' ? supa() : null);
      var em = signedInEmail();
      var pin = readPin();
      var requestId = (function(){
        try {
          /* Stable per-attempt id so retries dedupe but each fresh
             apply gets its own. Format: timestamp-random. */
          return 'apply-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
        } catch(_){ return 'apply-' + Date.now(); }
      })();
      if(sb && em && pin && typeof sb.rpc === 'function'){
        sb.rpc('apply_pending_ticket', {
          user_email: em,
          user_pin: pin,
          character_id: characterId,
          client_request_id: requestId
        })
          .then(function(res){
            var data = res && res.data;
            var err  = res && res.error;
            if(err || !data || data.ok !== true){
              /* Server-side failure. Roll back EVERYTHING we did
                 optimistically so the user's actual state matches
                 reality. */
              try { console.warn('[Tickets] apply_pending_ticket failed', err || data); } catch(_){}
              rollbackOptimisticApply(characterId, opts);
              return;
            }
            /* Success path. The server returned authoritative
               new_tickets. Our optimistic +1 should match — but if the
               server diverged (e.g., another concurrent apply landed
               first or the row already had bonus tickets we weren't
               aware of), force-sync the local cache to the server
               value so refresh shows the same number. */
            var newTickets = (typeof data.new_tickets === 'number') ? data.new_tickets : null;
            /* R735: the server also reports the guest count, so a card that
               received someone else's ticket shows the right tier-up input. */
            var newGuest = (typeof data.new_guest_tickets === 'number') ? data.new_guest_tickets : null;
            if(newTickets !== null || newGuest !== null){
              syncCharacterTicketsToServer(characterId, newTickets, newGuest);
            }
            /* R687: the server decides whether this actively collected
               card was also submitted by this account. Only that case
               levels up, atomically with the ticket apply. */
            if(data.is_submitter === true && data.leveled_up === true){
              try {
                var rows = (typeof SOURCE_ROWS !== 'undefined' && SOURCE_ROWS && SOURCE_ROWS.characters) ? SOURCE_ROWS.characters : null;
                if(!rows && typeof window.hohGetAllCharacters === 'function') rows = window.hohGetAllCharacters() || [];
                if(rows){
                  for(var ri = 0; ri < rows.length; ri++){
                    if(rows[ri] && String(rows[ri].id) === String(characterId)){
                      rows[ri].level = data.level;
                      rows[ri].xp = data.xp;
                      if(typeof data.xp_to_next === 'number') rows[ri].xp_to_next = data.xp_to_next;
                      break;
                    }
                  }
                }
              } catch(_){}
              try {
                var levelCard = opts.targetEl && opts.targetEl.closest ? opts.targetEl.closest('.hoh-yh-card') : null;
                if(levelCard && typeof window.hohYhCelebrateTicketLevelUp === 'function'){
                  window.hohYhCelebrateTicketLevelUp(characterId, levelCard, data);
                }
              } catch(_){}
            }
            if(!opts.silent){
              var ticketTarget = (ticketHeroName && ticketHeroName !== 'this hero') ? ticketHeroName : 'this hero';
              var ticketToast = data.status === 'already'
                ? 'That ticket was already safely applied.'
                : (data.leveled_up === true
                  ? '+1 ticket applied to ' + ticketTarget + '. They reached Level ' + String(data.level || '') + ', and this card\'s Crystal tier-up odds increased.'
                  /* R734 (Jan's rule): only a hero you submitted gains a raffle
                     entry from a ticket. Anyone else's card gains tier-up odds only. */
                  : (data.is_submitter === true
                    ? '+1 ticket applied to ' + ticketTarget + '. This card\'s Crystal tier-up odds and your raffle chance increased.'
                    : '+1 ticket applied to ' + ticketTarget + '. This card\'s Crystal tier-up odds increased.'));
              try { hohShowHallToast && hohShowHallToast(ticketToast); } catch(_){}
            }
          })
          .catch(function(e){
            try { console.warn('[Tickets] apply_pending_ticket threw', e); } catch(_){}
            rollbackOptimisticApply(characterId, opts);
          });
      } else {
        /* No auth → can't persist server-side. The user is signed-out
           or the RPC client isn't loaded. Roll back so we don't pretend
           we saved when we couldn't. */
        rollbackOptimisticApply(characterId, opts);
      }
    } catch(_){
      rollbackOptimisticApply(characterId, opts);
    }
    return true;
  };

  /* R137: Roll back the optimistic state we set in hohApplyTicket
     when the server write fails. Refunds the wallet ticket, reverts
     c.tickets in every cache, repaints the breakdown + counters,
     and toasts the user so they know to retry. */
  function rollbackOptimisticApply(characterId, opts){
    try {
      /* 1. Refund the wallet — restore the ticket the user spent. */
      if(typeof window.hohRefundUnspentTicket === 'function'){
        try { window.hohRefundUnspentTicket(); } catch(_){}
      }
      /* 2. Revert the optimistic bump on every cache. Mirrors the bump
         path in hohApplyTicket but with delta = -1, on the same field
         (R735: guest_tickets when the hero was not the player's own). */
      var rbField = (opts && opts.mine === false) ? 'guest_tickets' : 'tickets';
      try {
        if(window.hohTicketState && Array.isArray(window.hohTicketState.my_characters)){
          window.hohTicketState.my_characters.forEach(function(r){
            if(r && String(r.id) === String(characterId)) r[rbField] = Math.max(0, (r[rbField] || 0) - 1);
          });
        }
      } catch(_){}
      try {
        if(typeof window.hohGetAllCharacters === 'function'){
          var pool = window.hohGetAllCharacters() || [];
          for(var i = 0; i < pool.length; i++){
            if(pool[i] && String(pool[i].id) === String(characterId)){
              pool[i][rbField] = Math.max(0, (pool[i][rbField] || 0) - 1);
              break;
            }
          }
        }
      } catch(_){}
      /* 3. Revert the visual chip on the detail modal pill if open. */
      try {
        var detailPill = document.getElementById('hoh-detail-tk-pill');
        if(rbField === 'tickets' && detailPill && String(detailPill.getAttribute('data-character-id')) === String(characterId)){
          var detailCount = document.getElementById('hoh-detail-tk-count');
          if(detailCount){
            var dn = parseInt(detailCount.textContent || '0', 10) || 0;
            detailCount.textContent = String(Math.max(0, dn - 1));
          }
        }
      } catch(_){}
      /* 4. Re-render the breakdown + header + dispatch event so card
         pills repaint to the rolled-back values. */
      try { liveUpdateBreakdown(characterId); } catch(_){}
      try { renderHeaderCounter(); } catch(_){}
      try {
        document.dispatchEvent(new CustomEvent('hoh:ticket-apply-failed', {
          detail: { characterId: characterId }
        }));
      } catch(_){}
      /* 5. Tell the user honestly. Two scenarios:
           - Network/RPC error: try again
           - RPC missing on server: nothing the user can do, but
             they should know their ticket wasn't applied
         We use a single message because the user-side action is the
         same in both cases (try again later). */
      try {
        if(typeof window.hohShowHallToast === 'function'){
          window.hohShowHallToast('Ticket not saved. Please try again.');
        }
      } catch(_){}
    } catch(_){}
  }

  /* R137: Sync local c.tickets to the server's authoritative count
     after a successful apply. Usually a no-op (the optimistic +1
     already matched the server's bump), but if the server returned
     a different value (concurrent applies, mythic bonus stored
     server-side, etc.), force the cache to match so refresh-cycles
     show the right number. */
  function syncCharacterTicketsToServer(characterId, newTickets, newGuest){
    var haveTix = (typeof newTickets === 'number' && newTickets >= 0);
    var haveGuest = (typeof newGuest === 'number' && newGuest >= 0);
    if(!haveTix && !haveGuest) return;
    function put(r){
      if(haveTix) r.tickets = newTickets;
      if(haveGuest) r.guest_tickets = newGuest;
    }
    try {
      if(window.hohTicketState && Array.isArray(window.hohTicketState.my_characters)){
        window.hohTicketState.my_characters.forEach(function(r){
          if(r && String(r.id) === String(characterId)) put(r);
        });
      }
    } catch(_){}
    try {
      if(typeof window.hohGetAllCharacters === 'function'){
        var pool = window.hohGetAllCharacters() || [];
        for(var i = 0; i < pool.length; i++){
          if(pool[i] && String(pool[i].id) === String(characterId)){
            put(pool[i]);
            break;
          }
        }
      }
    } catch(_){}
    /* Re-render breakdown so its total + win % reflects the
       authoritative count. */
    try { liveUpdateBreakdown(characterId); } catch(_){}
  }

  /* R64: Fly a ticket sprite from the header pill to a target DOM
     element (a character card, the breakdown badge, etc.). Mirrors
     flyBountyTicketToPill but in reverse direction: pill → target. */
  // R754: presentation follows a confirmed receipt. It never spends or changes a balance.
  var battleSpendSeen = new Set();
  function battleTicketAnchor() {
    var candidates = ['ffshop-bg-ticket','hoh-tk-header-bg-row','hoh-tk-header'].map(function(id){return document.getElementById(id);}).filter(Boolean);
    for (var i=0;i<candidates.length;i++) {
      var r=candidates[i].getBoundingClientRect();
      if(r.width&&r.height&&r.bottom>0&&r.top<innerHeight&&r.right>0&&r.left<innerWidth)
        return {x:Math.max(28,Math.min(innerWidth-28,r.left+r.width/2)),y:Math.max(28,Math.min(innerHeight-28,r.top+r.height/2))};
    }
    return null;
  }
  window.hohCaptureBattleTicketAnchor = battleTicketAnchor;
  document.addEventListener('hoh:bg-ticket-spent',function(event){
    var detail=event.detail||{},id=String(detail.id||'');
    if(detail.confirmed!==true||!id||battleSpendSeen.has(id)||document.hidden)return;
    battleSpendSeen.add(id);if(battleSpendSeen.size>128)battleSpendSeen.delete(battleSpendSeen.values().next().value);
    var start=detail.origin||battleTicketAnchor(),target=document.getElementById('hocUStart')||document.querySelector('#hoh-contract .hoc-ueyebrow');
    var r=target&&target.getBoundingClientRect(),end={x:innerWidth/2,y:Math.min(innerHeight-80,innerHeight*.4)};
    if(r&&r.width&&r.top>=0&&r.bottom<=innerHeight)end={x:r.left+r.width/2,y:r.top+r.height/2};
    var quiet=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var el=document.createElement('div'),animations=[],timer;
    el.className='hoh-battle-ticket-spend';el.setAttribute('role','status');el.setAttribute('aria-label','1 Battlegrounds ticket used');
    el.style.cssText='position:fixed;z-index:2147483000;pointer-events:none;display:flex;align-items:center;gap:8px;color:#f3dba0;font:600 12px Georgia,serif;white-space:nowrap;filter:drop-shadow(0 2px 8px #120e16);';
    el.innerHTML='<svg width="46" height="28" viewBox="0 0 46 28" aria-hidden="true"><path d="M3 2h40v7a5 5 0 0 0 0 10v7H3v-7A5 5 0 0 0 3 9Z" fill="#b8913e" stroke="#f5db93"/><path d="M32 5v18" stroke="#584425" stroke-dasharray="2 2"/><path d="m13 9 10 10m0-10L13 19m-2-2 4 4m6-14 4 4" fill="none" stroke="#332517" stroke-width="1.5"/></svg><span>1 ticket used</span>';
    document.body.appendChild(el);
    function clean(){clearTimeout(timer);animations.forEach(function(a){try{a.cancel();}catch(_){}});el.remove();document.removeEventListener('visibilitychange',hidden);}
    function hidden(){if(document.hidden)clean();}
    document.addEventListener('visibilitychange',hidden);
    if(quiet||!start||typeof el.animate!=='function'){
      el.style.left=Math.max(10,Math.min(innerWidth-160,end.x-70))+'px';el.style.top=Math.max(16,end.y-48)+'px';timer=setTimeout(clean,1100);return;
    }
    el.style.left=(start.x-23)+'px';el.style.top=(start.y-14)+'px';
    var dx=end.x-start.x,dy=end.y-start.y;
    var frames=[];
    for(var frame=0;frame<=24;frame++){
      var progress=frame/24,travel=1-Math.pow(1-progress,2);
      frames.push({transform:'translate('+(dx*travel)+'px,'+(dy*travel-Math.sin(travel*Math.PI)*52)+'px) rotate('+(-8*(1-travel))+'deg) scale('+(1-.15*travel)+')',opacity:Math.min(1,progress*8),offset:progress*.8});
    }
    frames.push({transform:'translate('+dx+'px,'+dy+'px) scale(.6)',opacity:0,offset:1});
    animations.push(el.animate(frames,{duration:900,easing:'linear',fill:'forwards'}));
    for(var n=0;n<12;n++){
      var mote=document.createElement('i'),angle=n*Math.PI*2/12;
      mote.style.cssText='position:absolute;left:22px;top:14px;width:3px;height:3px;border-radius:50%;background:#ffdea0;box-shadow:0 0 5px #d9b15a;';el.appendChild(mote);
      animations.push(mote.animate([{transform:'translate(0,0)',opacity:0},{opacity:.9,offset:.2},{transform:'translate('+(Math.cos(angle)*44)+'px,'+(Math.sin(angle)*32)+'px)',opacity:0}],{delay:510+n*7,duration:330,fill:'both'}));
    }
    timer=setTimeout(clean,1050);
  });

  function flyTicketFromPillTo(targetEl){
    var pill = document.getElementById('hoh-tk-header');
    if(!pill) return;
    var pillRect = pill.getBoundingClientRect();
    var startX = pillRect.left + pillRect.width / 2;
    var startY = pillRect.top + pillRect.height / 2;
    var endX, endY;
    if(targetEl && typeof targetEl.getBoundingClientRect === 'function'){
      var tr = targetEl.getBoundingClientRect();
      if(tr && tr.width){
        endX = tr.left + tr.width / 2;
        endY = tr.top + tr.height / 2;
      }
    }
    if(endX == null || endY == null){
      endX = window.innerWidth / 2;
      endY = window.innerHeight * 0.5;
    }
    var el = document.createElement('div');
    el.className = 'hoh-ticket-apply-fly';
    el.innerHTML = '<span class="hoh-tk-icon" aria-hidden="true"></span><span>+1</span>';
    el.style.left = startX + 'px';
    el.style.top = startY + 'px';
    document.body.appendChild(el);
    requestAnimationFrame(function(){
      el.style.transform = 'translate(' + (endX - startX) + 'px,' + (endY - startY) + 'px) scale(0.7)';
      el.style.opacity = '0';
    });
    setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 1100);
    /* Pulse the target element so the user sees IT receive the
       ticket, not just a sprite drifting past. */
    if(targetEl && targetEl.classList){
      try {
        targetEl.classList.remove('hoh-ticket-apply-target-pulse');
        void targetEl.offsetWidth;
        targetEl.classList.add('hoh-ticket-apply-target-pulse');
        setTimeout(function(){ targetEl.classList.remove('hoh-ticket-apply-target-pulse'); }, 1100);
      } catch(_){}
    }
  }

  /* R64: When a ticket is applied while the breakdown lightbox is
     open, re-build its inner HTML against the freshly-bumped
     character so the user sees the total + win % update live. We
     locate the open breakdown by its data-character-id attribute on
     the parent .hoh-tk-tip wrapper. */
  function liveUpdateBreakdown(characterId){
    if(!characterId) return;
    /* Find every open breakdown tip pointing at this character. */
    var tips = document.querySelectorAll('.hoh-tk-tip-open[data-character-id="' + String(characterId).replace(/"/g, '\\"') + '"], .hoh-tk-tip[data-character-id="' + String(characterId).replace(/"/g, '\\"') + '"]');
    if(!tips || !tips.length) return;
    /* Resolve the just-bumped character from any cache. */
    var c = null;
    try {
      if(typeof window.hohGetAllCharacters === 'function'){
        var pool = window.hohGetAllCharacters() || [];
        for(var i = 0; i < pool.length; i++){ if(pool[i] && String(pool[i].id) === String(characterId)){ c = pool[i]; break; } }
      }
    } catch(_){}
    if(!c){
      try {
        var rows = (window.hohTicketState && window.hohTicketState.my_characters) || [];
        for(var j = 0; j < rows.length; j++){ if(rows[j] && String(rows[j].id) === String(characterId)){ c = rows[j]; break; } }
      } catch(_){}
    }
    if(!c) return;
    if(typeof window.hohBuildTicketBreakdownHtml !== 'function') return;
    var html = window.hohBuildTicketBreakdownHtml(c);
    /* The breakdown HTML lives inside .hoh-tk-bd-inner; replace its
       contents on each open tip. */
    for(var k = 0; k < tips.length; k++){
      var tip = tips[k];
      var inner = tip.querySelector('.hoh-tk-bd-inner');
      if(inner){
        inner.innerHTML = html;
      } else {
        /* Fallback: replace the tip\'s entire body. */
        tip.innerHTML = '<div class="hoh-tk-bd-inner">' + html + '</div>';
      }
    }
  }

  /* Public hook called by hohCallHeartRpc in the section. Receives the
     ticket sub-object from the RPC's JSON return. Updates the cache,
     re-renders the header, and fires a celebration if a ticket was
     just awarded. */
  window.hohOnTicketResult = function(ticket){
    if(!ticket || typeof ticket !== 'object') return;
    if(typeof ticket.tickets_today === 'number'){
      window.hohTicketState.tickets_today = ticket.tickets_today;
    }
    if(typeof ticket.tickets_today_max === 'number'){
      window.hohTicketState.tickets_today_max = ticket.tickets_today_max;
    }
    if(typeof ticket.hearts_given === 'number'){
      window.hohTicketState.hearts_given_today = ticket.hearts_given;
    }
    renderHeaderCounter();
    if(ticket.ticket_awarded){
      celebrateTicketEarned();
    }
  };

  /* Called by the section's submit handler after a successful insert.
     Marks submission done locally and fires the celebration so the
     pill flips immediately, then refreshes server state in the
     background to confirm.
     R62: dispatch hoh:ticket-state-changed so the Daily Tickets
     panel + any other listeners pick up the submitted_today flip
     without waiting for a full page interaction. */
  window.hohOnSubmissionTicket = function(){
    window.hohTicketState.submitted_today = true;
    renderHeaderCounter();
    celebrateTicketEarned();
    try {
      document.dispatchEvent(new CustomEvent('hoh:ticket-state-changed', {
        detail: { source: 'submission' }
      }));
    } catch(_){}
    if(typeof window.hohFetchRaffleState === 'function'){
      window.hohFetchRaffleState();
    }
  };

  /* Header pill renderer. Now shows TWO rows in one box:
       Row 1: ticket count (X/4 tickets today)
       Row 2: heart count (♡ X hearts left)
     Click anywhere opens the Earn Tickets overview. The legacy floating
     #hoh-heart-remaining counter is hidden via CSS. */
  function renderHeaderCounter(){
    /* R148-Z41: First-timer gate. The wallet pill only exists for
       signed-in visitors (anon visitors don't even know hearts are
       a thing). If a pill was already injected and the user has
       since signed out (rare — wipe localStorage in dev tools, say),
       remove it. Otherwise bail before creating. The CSS in the
       section template also hides the pill via display:none when
       html.hoh-hall-signed-in is absent, so this is belt-and-braces. */
    var __signedIn = (typeof window.hohIsHallSignedIn === 'function')
      ? window.hohIsHallSignedIn()
      : false;
    if(!__signedIn){
      var existing = document.getElementById('hoh-tk-header');
      if(existing && existing.parentNode) existing.parentNode.removeChild(existing);
      return;
    }
    var d = computeDailyTotal();
    var heartsLeft = (typeof window.heartsRemainingToday === 'function')
      ? window.heartsRemainingToday() : 0;
    var pill = document.getElementById('hoh-tk-header');
    if(!pill){
      pill = document.createElement('div');
      pill.id = 'hoh-tk-header';
      pill.setAttribute('role', 'group');
      pill.setAttribute('aria-label', 'Wallet');
      pill.className = 'hoh-tk hoh-tk-header-pill hoh-tk-header-fixed';
      pill.onclick = function(){
        if(typeof window.hohOpenEarnTickets === 'function'){
          window.hohOpenEarnTickets();
        } else if(typeof window.hohOpenTrial === 'function'){
          window.hohOpenTrial();
        }
      };
      pill.innerHTML =
        '<button type="button" class="hoh-tk-wallet-toggle" id="hoh-tk-header-toggle" aria-controls="hoh-tk-header-content" aria-expanded="true" aria-label="Collapse wallet">' +
          '<svg class="hoh-tk-pouch" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m8 3 1.5 5h5L16 3l-4 1-4-1Z"/><path d="M9.5 8C8 10 5 13 5 16.5 5 20 7.5 21 12 21s7-1 7-4.5C19 13 16 10 14.5 8M8.5 8h7M14.5 8l2 3"/></svg>' +
          '<span class="hoh-tk-wallet-title">Wallet</span><span class="hoh-tk-wallet-minus" aria-hidden="true">&minus;</span>' +
        '</button>' +
        '<div class="hoh-tk-wallet-content" id="hoh-tk-header-content">' +
        '<button type="button" class="hoh-tk-wallet-resources" id="hoh-tk-header-resources">' +
        '<div class="hoh-tk-row hoh-tk-row-ticket">' +
          '<span class="hoh-tk-icon" aria-hidden="true"></span>' +
          '<span class="hoh-tk-count" id="hoh-tk-header-count">0/4</span>' +
          '<span class="hoh-tk-label" id="hoh-tk-header-label">tickets today</span>' +
        '</div>' +
        /* R712: earned packs can wait in the server wallet. Surface that
           count in the existing wallet pill so leaving a reward screen never
           makes the pack look lost. Zero stays quiet like the other rows. */
        '<div class="hoh-tk-row hoh-tk-row-pack" id="hoh-tk-header-pack-row" style="display:none;">' +
          '<span class="hoh-tk-pack-icon" aria-hidden="true"><i></i><i></i><i></i></span>' +
          '<span class="hoh-tk-count" id="hoh-tk-header-pack-count">0</span>' +
          '<span class="hoh-tk-label" id="hoh-tk-header-pack-label">unopened packs</span>' +
        '</div>' +
        /* R66: Wallet row — appears between the daily-progress row and
           the hearts row when the user has unspent tickets to spend.
           Click → opens the same Daily Tickets modal which now leads
           into Your Hall to distribute. Hidden via display:none when
           wallet === 0 (set in the render pass below). */
        '<div class="hoh-tk-row hoh-tk-row-wallet" id="hoh-tk-header-wallet-row" style="display:none;">' +
          '<span class="hoh-tk-icon hoh-tk-icon-wallet" aria-hidden="true"></span>' +
          '<span class="hoh-tk-count" id="hoh-tk-header-wallet-count">0</span>' +
          '<span class="hoh-tk-label">to spend</span>' +
        '</div>' +
        /* R376: Battlegrounds entry row. Shown only when the player holds
           one, same quiet rule as the wallet row above.
           R391 (Jan): renamed entries, and the row moved UP — it lives with
           the tickets group, above the ruled line, hearts alone below it. */
        '<div class="hoh-tk-row hoh-tk-row-bg" id="hoh-tk-header-bg-row" style="display:none;">' +
          '<span class="hoh-tk-bg-icon" aria-hidden="true">&#9876;&#xFE0E;</span>' +
          '<span class="hoh-tk-count" id="hoh-tk-header-bg-count">0</span>' +
          '<span class="hoh-tk-label" id="hoh-tk-header-bg-label">battlegrounds entries</span>' +
        '</div>' +
        /* ⛔ R597 (Jan): "if they have none then dont show it as a row only show
           crystal icon with a number next to it when they have any crystals in
           their inventory." Hidden at zero, exactly the quiet rule the wallet
           and Battlegrounds rows above already follow, so a player who has
           never touched crystals never sees a word about them and the widget
           stays the height it is today. */
        '<div class="hoh-tk-row hoh-tk-row-xtal" id="hoh-tk-header-xtal-row" style="display:none;">' +
          '<span class="hoh-tk-xtal-icon" id="hoh-tk-header-xtal-icon" aria-hidden="true"></span>' +
          '<span class="hoh-tk-count" id="hoh-tk-header-xtal-count">0</span>' +
          '<span class="hoh-tk-label" id="hoh-tk-header-xtal-label">mythic crystals</span>' +
        '</div>' +
        '<div class="hoh-tk-row hoh-tk-row-heart" id="hoh-tk-header-heart-row">' +
          '<span class="hoh-tk-heart-icon" aria-hidden="true">&#9829;&#xFE0E;</span>' +
          '<span class="hoh-tk-heart-count" id="hoh-tk-header-heart-count">0</span>' +
          '<span class="hoh-tk-heart-label">hearts left</span>' +
        '</div>' +
        '</button>' +
        /* R148-Z26 Round 4: Inventory chip row. Hidden when the pouch
           is empty so brand-new users never see clutter. Re-renders on
           the 'hoh:inventory-changed' event (dispatched by add/consume)
           and on every renderHeaderCounter call. */
        '<div class="hoh-tk-row hoh-tk-row-inventory" id="hoh-tk-header-inv-row" style="display:none;"></div></div>' +
        '<span class="hoh-tk-tooltip" id="hoh-tk-header-tooltip" role="tooltip" aria-hidden="true"></span>';
      var toggle = pill.querySelector('#hoh-tk-header-toggle');
      var content = pill.querySelector('#hoh-tk-header-content');
      function setCollapsed(collapsed){
        pill.classList.toggle('is-collapsed', collapsed);
        content.hidden = collapsed;
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.setAttribute('aria-label', collapsed ? 'Expand wallet' : 'Collapse wallet');
        toggle.title = collapsed ? 'Expand wallet' : 'Collapse wallet';
      }
      function saveCollapsed(collapsed){
        setCollapsed(collapsed);
        try { localStorage.setItem('hoh-wallet-collapsed', collapsed ? '1' : '0'); } catch(_){}
      }
      var preference = null;
      try { preference = localStorage.getItem('hoh-wallet-collapsed'); } catch(_){}
      setCollapsed(preference === '1' || (preference === null && window.matchMedia && window.matchMedia('(max-width:700px)').matches));
      toggle.onclick = function(event){
        event.stopPropagation();
        saveCollapsed(!pill.classList.contains('is-collapsed'));
      };
      pill.addEventListener('keydown', function(event){
        if(event.key === 'Escape' && !pill.classList.contains('is-collapsed')){
          event.stopPropagation();
          saveCollapsed(true);
          toggle.focus();
        }
      });
      document.body.appendChild(pill);
    }
    /* R148-Z26 fix: Subscribe to inventory-changed AT MOST ONCE per
       page load — and outside the `if(!pill)` block so that a pill
       created before the inventory module loaded still wires up its
       listener on the next render call. Module-level flag guards
       against duplicate subscriptions on subsequent renderHeaderCounter
       passes. */
    try {
      /* R597: buying or spending a crystal must move this row without waiting
         for something else to repaint the pill. */
      if(!window.__hohXtalListenerBound){
        window.__hohXtalListenerBound = true;
        document.addEventListener('hoh:crystals-changed', function(){
          try { renderHeaderCounter(); } catch(_){}
        });
      }
      if(!window.__hohWalletHeaderListenerBound){
        window.__hohWalletHeaderListenerBound = true;
        document.addEventListener('hoh:wallet-synced', function(){
          try { renderHeaderCounter(); } catch(_){}
        });
        document.addEventListener('hoh:packs-changed', function(){
          try { renderHeaderCounter(); } catch(_){}
        });
      }
      if(!window.__hohInvListenerBound){
        window.__hohInvListenerBound = true;
        document.addEventListener('hoh:inventory-changed', function(){
          try { renderHeaderCounter(); } catch(_){}
        });
      }
    } catch(_){}
    var count = document.getElementById('hoh-tk-header-count');
    var label = document.getElementById('hoh-tk-header-label');
    var heartCount = document.getElementById('hoh-tk-header-heart-count');
    var resources = document.getElementById('hoh-tk-header-resources');
    var tip = document.getElementById('hoh-tk-header-tooltip');
    var W = window.HOHWallet || null;
    var unopenedPacks = 0;
    if(count) count.textContent = d.total + '/' + d.max;
    if(heartCount) heartCount.textContent = heartsLeft;
    try {
      unopenedPacks = (W && typeof W.packsUnopened === 'function') ? Math.max(0, W.packsUnopened() | 0) : 0;
      var packRow = document.getElementById('hoh-tk-header-pack-row');
      if(packRow){
        if(unopenedPacks > 0){
          packRow.style.display = '';
          var packCount = document.getElementById('hoh-tk-header-pack-count');
          var packLabel = document.getElementById('hoh-tk-header-pack-label');
          if(packCount) packCount.textContent = String(unopenedPacks);
          if(packLabel) packLabel.textContent = unopenedPacks === 1 ? 'unopened pack' : 'unopened packs';
        } else {
          packRow.style.display = 'none';
        }
      }
    } catch(_){ unopenedPacks = 0; }
    /* R66: Update the wallet row visibility + count. Show when
       unspent > 0 so the user sees their distributable inventory at
       a glance; hide when 0 to reduce visual noise. */
    var unspent = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
    var walletRow = document.getElementById('hoh-tk-header-wallet-row');
    var walletCountEl = document.getElementById('hoh-tk-header-wallet-count');
    if(walletRow){
      if(unspent > 0){
        walletRow.style.display = '';
        if(walletCountEl) walletCountEl.textContent = String(unspent);
      } else {
        walletRow.style.display = 'none';
      }
    }
    /* R376: Battlegrounds entry row mirrors the local wallet. */
    var bgHeld = (typeof window.hohGetBgTickets === 'function') ? window.hohGetBgTickets() : 0;
    var bgRow = document.getElementById('hoh-tk-header-bg-row');
    var bgCountEl = document.getElementById('hoh-tk-header-bg-count');
    if(bgRow){
      if(bgHeld > 0){
        bgRow.style.display = '';
        if(bgCountEl) bgCountEl.textContent = String(bgHeld);
        /* R391: singular and plural both read correctly */
        var bgLblEl = document.getElementById('hoh-tk-header-bg-label');
        if(bgLblEl) bgLblEl.textContent = bgHeld === 1 ? 'battlegrounds entry' : 'battlegrounds entries';
      } else {
        bgRow.style.display = 'none';
      }
    }
    /* R597: the crystal row mirrors the Battlegrounds row above, including its
       singular and plural, and repaints on hoh:crystals-changed. */
    try{
      var xRow = document.getElementById('hoh-tk-header-xtal-row');
      if(xRow){
        var xHeld = (W && W.crystals) ? W.crystals() : 0;
        if(xHeld > 0){
          xRow.style.display = '';
          var xC = document.getElementById('hoh-tk-header-xtal-count');
          if(xC) xC.textContent = String(xHeld);
          var xL = document.getElementById('hoh-tk-header-xtal-label');
          if(xL) xL.textContent = xHeld === 1 ? 'mythic crystal' : 'mythic crystals';
          var xI = document.getElementById('hoh-tk-header-xtal-icon');
          /* ⛔ painted once, not on every repaint. renderHeaderCounter runs on
             every hearts bump, and rewriting an SVG a few times a second is
             work nobody asked for. */
          if(xI && !xI.firstChild && W && W.icon) xI.innerHTML = W.icon(15);
        } else {
          xRow.style.display = 'none';
        }
      }
    }catch(_){}

    var capped = d.total >= d.max;
    var packAria = unopenedPacks > 0 ? ', ' + unopenedPacks + (unopenedPacks === 1 ? ' unopened pack' : ' unopened packs') : '';
    if(capped){
      pill.classList.add('hoh-tk-capped');
      resources.setAttribute('aria-label', 'Daily quests complete: ' + d.max + ' of ' + d.max + ' tickets earned' + packAria + '. Open resources');
      if(label) label.textContent = 'Daily complete';
    } else {
      pill.classList.remove('hoh-tk-capped');
      resources.setAttribute('aria-label', d.total + ' of ' + d.max + ' daily tickets earned, ' + heartsLeft + ' hearts left' + packAria + '. Open resources');
      if(label) label.textContent = 'tickets today';
    }
    /* R148-Z29: Inventory items render as inline rows that match the
       hearts/tickets row format — same icon size, same font, same
       row layout. No separator border, no separate pill chrome. The
       user wants them to flow as natural data lines in the pill.
       Each owned item renders one row; a claimable Oath becomes a
       clickable claim button row instead. */
    try {
      var invRow = document.getElementById('hoh-tk-header-inv-row');
      if(invRow){
        var rowsHtml = '';
        /* R148-Z30: Replaced emoji icons (fire/scroll/chain) with
           text-glyph Unicode that respects font-size.
           R150-Z45: Inventory items STACK. Each chip now shows a ×N
           quantity badge when the player holds more than one, so a
           repeat purchase is visibly tracked instead of looking like
           it vanished. Counts come from hohInventoryCount. */
        var invCount = (typeof window.hohInventoryCount === 'function')
          ? window.hohInventoryCount
          : function(){ return 0; };
        var qtyBadge = function(n){
          return n > 1 ? ' <span class="hoh-tk-inv-qty">&times;' + n + '</span>' : '';
        };
        var lonefireN = invCount('lonefireCharm');
        if(lonefireN > 0){
          rowsHtml += '<div class="hoh-tk-row hoh-tk-row-inv-item hoh-tk-row-inv-lonefire" title="Lonefire Charm. It steps in when your hearts run out. You hold ' + lonefireN + '.">' +
            '<span class="hoh-tk-inv-icon" aria-hidden="true">&#10026;</span>' +
            '<span class="hoh-tk-inv-name">Lonefire' + qtyBadge(lonefireN) + '</span>' +
            '<span class="hoh-tk-inv-sub">embers ready</span>' +
          '</div>';
        }
        var scrollN = invCount('scrollOfLineage');
        if(scrollN > 0){
          rowsHtml += '<div class="hoh-tk-row hoh-tk-row-inv-item hoh-tk-row-inv-lineage" title="Scroll of Lineage — one free hero unlock each. You hold ' + scrollN + '.">' +
            '<span class="hoh-tk-inv-icon" aria-hidden="true">&#167;</span>' +
            '<span class="hoh-tk-inv-name">Scroll' + qtyBadge(scrollN) + '</span>' +
            '<span class="hoh-tk-inv-sub">free unlock</span>' +
          '</div>';
        }
        var oathStatus = (typeof window.hohInventoryOathStatus === 'function') ? window.hohInventoryOathStatus() : null;
        if(oathStatus){
          var oathN = oathStatus.count || 1;
          if(oathStatus.claimable){
            rowsHtml += '<button type="button" class="hoh-tk-row hoh-tk-row-inv-item hoh-tk-row-inv-oath hoh-tk-row-inv-oath-claim" title="Patron\'s Oath is ready. Click to claim it. You hold ' + oathN + '." onclick="event.stopPropagation();(function(){if(window.hohInventoryClaimOath&&window.hohInventoryClaimOath()){try{var pill=document.getElementById(\'hoh-tk-header\');if(pill&&window.flyDiceTraderHeartsToPill)window.flyDiceTraderHeartsToPill(pill,12);}catch(e){}}})();">' +
              '<span class="hoh-tk-inv-icon" aria-hidden="true">&#10038;</span>' +
              '<span class="hoh-tk-inv-name">Claim Oath' + qtyBadge(oathN) + '</span>' +
              '<span class="hoh-tk-inv-sub">ready to claim</span>' +
            '</button>';
          } else {
            var daysLeft = Math.max(0, 5 - (oathStatus.daysHeld || 0));
            rowsHtml += '<div class="hoh-tk-row hoh-tk-row-inv-item hoh-tk-row-inv-oath" title="Patron\'s Oath held. ' + daysLeft + ' day' + (daysLeft === 1 ? '' : 's') + ' until you can claim it. You hold ' + oathN + '.">' +
              '<span class="hoh-tk-inv-icon" aria-hidden="true">&#10038;</span>' +
              '<span class="hoh-tk-inv-name">Oath' + qtyBadge(oathN) + '</span>' +
              '<span class="hoh-tk-inv-sub">' + daysLeft + 'd left</span>' +
            '</div>';
          }
        }
        if(rowsHtml){
          invRow.style.display = '';
          invRow.innerHTML = rowsHtml;
        } else {
          invRow.style.display = 'none';
          invRow.innerHTML = '';
        }
      }
    } catch(_){}
    if(tip){
      var missing = [];
      if(!d.submitted) missing.push('Submit a hero <span class="hoh-tk-tooltip-pill">+1</span>');
      /* R61: wander tooltip reflects how many of the 2 tickets are
         still unclaimed instead of treating it as one binary "Explore
         the Hall +2" line. */
      var wt = d.wanderTickets || 0;
      /* R140-Z: Wander now has 3 ticket milestones (10/20/40); the
         tooltip suggests Explore the Hall when any are unclaimed. */
      if(wt < 3){
        var pillN = 3 - wt;
        var wantPill = '+' + pillN;
        missing.push('Explore the Hall <span class="hoh-tk-tooltip-pill">' + wantPill + '</span>');
      }
      /* R140-Z: Daily Bounty removed as a ticket source. */
      if(missing.length){
        /* Touch devices say "Tap", pointer devices say "Click". Detect
           via matchMedia(hover:none) which is true on touch-only. */
        var verb = 'Click';
        try { if(window.matchMedia('(hover:none)').matches) verb = 'Tap'; } catch(e){}
        tip.innerHTML = '<div class="hoh-tk-tooltip-title">Missing today</div>' + missing.map(function(m){ return '<div class="hoh-tk-tooltip-row">' + m + '</div>'; }).join('') + '<div class="hoh-tk-tooltip-foot">' + verb + ' to open resources and daily quests</div>';
      } else {
        tip.innerHTML = '<div class="hoh-tk-tooltip-title">All quests done</div><div class="hoh-tk-tooltip-foot">Open resources. Daily quests reset at midnight UTC.</div>';
      }
    }
    /* R793: the live Hall's pass runs last and overrides the rows above. */
    if(r793Live()) r793PaintHeader(pill, resources, tip, unopenedPacks);
  }

  /* R793 (Jan, 2026-09-22): "Heart wallet balances disappear completely."
     The hearts row and the ticket wallet row go. The ticket row stops
     counting earned tickets (nothing is earned) and instead says how many
     of YOUR heroes can still take today's ticket, straight from
     hoh_hall_ticket_state. Packs, crystals and Battlegrounds entries stay:
     they are the game's, and the game still runs on them. When nothing is
     left to show, the pill is not shown at all. */
  var _r793HeaderAsked = '';
  function r793PaintHeader(pill, resources, tip, packs){
    var D = window.HOHHallDoors;
    ['hoh-tk-header-heart-row', 'hoh-tk-header-wallet-row'].forEach(function(id){
      var el = document.getElementById(id); if(el) el.style.display = 'none';
    });
    var tkRow = pill.querySelector('.hoh-tk-row-ticket');
    var count = document.getElementById('hoh-tk-header-count');
    var label = document.getElementById('hoh-tk-header-label');
    var st = D ? D.tickets.cached() : null;
    var ids = st ? Object.keys(st.heroes) : [];
    var left = 0;
    ids.forEach(function(id){ if(!st.heroes[id].applied_today) left++; });
    if(tkRow){
      if(!st || !ids.length){ tkRow.style.display = 'none'; }
      else {
        tkRow.style.display = '';
        if(left > 0){
          if(count) count.textContent = String(left);
          if(label) label.textContent = left === 1 ? 'ticket to give today' : 'tickets to give today';
        } else {
          if(count) count.textContent = String(ids.length);
          if(label) label.textContent = 'given today';
        }
      }
    }
    pill.classList.toggle('hoh-tk-capped', !!(st && ids.length && left === 0));
    var parts = [];
    if(st && ids.length) parts.push(left > 0 ? (left + (left === 1 ? ' ticket' : ' tickets') + ' to give today') : ('all ' + ids.length + ' tickets given today'));
    if(packs > 0) parts.push(packs + (packs === 1 ? ' unopened pack' : ' unopened packs'));
    if(resources) resources.setAttribute('aria-label', (parts.length ? parts.join(', ') + '. ' : '') + 'Open resources');
    if(tip){
      tip.innerHTML = '<div class="hoh-tk-tooltip-title">' + ((st && ids.length && left > 0) ? 'Tickets to give' : 'Raffle tickets') + '</div>' +
        '<div class="hoh-tk-tooltip-row">Each of your heroes takes one ticket a day. Open your hero in the Hall and tap Give today&rsquo;s ticket.</div>' +
        '<div class="hoh-tk-tooltip-foot">The day turns at midnight Pacific time.</div>';
    }
    var rows = pill.querySelectorAll('.hoh-tk-wallet-resources > .hoh-tk-row, .hoh-tk-row-inventory');
    var any = false;
    for(var i = 0; i < rows.length; i++){ if(rows[i].style.display !== 'none') { any = true; break; } }
    pill.classList.toggle('hoh-tk-r793-empty', !any);
    /* ask once per account for today's state; the answer repaints the pill */
    var acct = (D && D.creds().email) || '';
    if(D && acct && !st && _r793HeaderAsked !== acct){
      _r793HeaderAsked = acct;
      D.tickets.state().then(function(){ try { renderHeaderCounter(); } catch(_){} });
    }
  }
  try {
    document.addEventListener('hoh:hall-tickets-changed', function(){ try { if(r793Live()) renderHeaderCounter(); } catch(_){} });
    document.addEventListener('hoh:signed-in', function(){ _r793HeaderAsked = ''; });
  } catch(_){}

  /* Float a "+N hearts" bubble above the pill and pulse the heart row
     to confirm the bonus arrived. Called by the blessing flow when a
     Legendary or Mythic detail-open grants extra hearts. */
  function pulseHeartsBumped(grantedCount){
    if(r793Live()) return; /* R793: no heart wallet on screen to pulse */
    var pill = document.getElementById('hoh-tk-header');
    if(!pill) return;
    var rect = pill.getBoundingClientRect();
    var bubble = document.createElement('div');
    bubble.className = 'hoh-tk-heart-bubble';
    bubble.style.cssText =
      'position:fixed;left:' + (rect.left + rect.width/2) + 'px;' +
      'top:' + rect.top + 'px;' +
      'transform:translate(-50%,-100%);pointer-events:none;z-index:99999;';
    bubble.innerHTML = '<span class="hoh-tk-heart-bubble-icon">&#9829;&#xFE0E;</span>';
    document.body.appendChild(bubble);
    setTimeout(function(){ if(bubble.parentNode) bubble.parentNode.removeChild(bubble); }, 1500);
    var heartRow = document.getElementById('hoh-tk-header-heart-row');
    if(heartRow){
      heartRow.classList.remove('hoh-tk-heart-pulse');
      void heartRow.offsetWidth;
      heartRow.classList.add('hoh-tk-heart-pulse');
      setTimeout(function(){ if(heartRow) heartRow.classList.remove('hoh-tk-heart-pulse'); }, 900);
    }
    /* Re-render so the heart count reflects the new pool size. */
    renderHeaderCounter();
  }
  window.hohPulseHeartsBumped = pulseHeartsBumped;

  /* ⛔ R648 · THE FAR END OF THE CRYSTAL'S FLIGHT.
     Jan: "there needs to be an animation and particle effect of it flying to
     your inventory right now its very lame." Half of "lame" was that nothing
     at the destination moved: the crystal faded out somewhere near the corner
     and a number changed silently behind it. hoh-shop.js flies the crystal and
     calls this the moment it lands.
     ⛔ IT MIRRORS pulseHeartsBumped ABOVE RATHER THAN INVENTING A SECOND
     ARRIVAL LANGUAGE. A heart arriving and a crystal arriving are the same
     kind of event and must read as one, so this is the same bubble, the same
     dwell, the same pulse, wearing the mythic colours instead of the pink.
     ⛔ IT LIVES HERE, NOT IN THE SHOP, because the pill's DOM is this file's.
     The reroll surface in hoh-contract-ui.js spends crystals and can call the
     same door when it needs to; two copies would drift the first time either
     is retuned. */
  function pulseCrystalsBumped(grantedCount){
    var pill = document.getElementById('hoh-tk-header');
    if(!pill) return;
    /* the count first, so the row and the bubble agree in the frame they move */
    try{ renderHeaderCounter(); }catch(_){}
    var row = document.getElementById('hoh-tk-header-xtal-row');
    /* ⛔ THE BUBBLE HANGS OFF THE PILL, THE PULSE SITS ON THE ROW, exactly as
       the hearts do. Anchoring the bubble to the ROW instead reads correctly
       and measures wrong: the row is INSIDE the pill, so the bubble starts
       below the pill's own top edge and spends its first frames floating over
       the counter it is trying to announce. Measured in
       harness/browser/xtalflytest.cjs, which is the only place that could
       have caught it. */
    var rect = pill.getBoundingClientRect();
    var n = Math.max(1, grantedCount|0);
    var W = window.HOHWallet;
    var bubble = document.createElement('div');
    bubble.className = 'hoh-tk-xtal-bubble';
    bubble.style.cssText =
      'position:fixed;left:' + (rect.left + rect.width/2) + 'px;' +
      'top:' + rect.top + 'px;' +
      'transform:translate(-50%,-100%);pointer-events:none;z-index:100093;';
    bubble.innerHTML = ((W && W.icon) ? W.icon(14) : '') + '+' + n;
    document.body.appendChild(bubble);
    setTimeout(function(){ if(bubble.parentNode) bubble.parentNode.removeChild(bubble); }, 1500);
    if(row){
      row.classList.remove('hoh-tk-xtal-pulse');
      void row.offsetWidth;
      row.classList.add('hoh-tk-xtal-pulse');
      setTimeout(function(){ if(row) row.classList.remove('hoh-tk-xtal-pulse'); }, 900);
    }
  }
  window.hohPulseCrystalsBumped = pulseCrystalsBumped;

  /* ============================================================
     LEGEND'S / MYTHIC BLESSING HOOK
     ============================================================
     window.hohOnDetailOpen(c) is called by the section's hohOpenDetail
     immediately after the modal becomes visible. We use it to grant a
     rarity blessing on the first open of a Legendary (+3 hearts) or
     Mythic (+5 hearts) hero PER CHARACTER PER DAY, plus a +10 first
     visit to the monthly Featured hero. Localstorage flags prevent
     farming via close + re-open.

     R150: the Daily Bounty solve-detection that used to live in this
     hook was removed along with the rest of the bounty code.

     Hearts are mostly vanity now (user pool is 100/day). The blessing
     animation is the meaningful feedback; the +N bonus is a small
     extra. */
  function todayYmd(){ var d = new Date(); return d.toISOString().slice(0, 10); }
  /* ISO week key (e.g. "2026-W18") so Legendary/Mythic blessings
     reset weekly instead of daily — gives the user a reason to
     revisit their favorite high-tier heroes each week. */
  function thisWeekKey(){
    var d = new Date();
    /* Copy to avoid mutating original. */
    var t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    /* Set to nearest Thursday: current date + 4 - current day number,
       Sunday=0 → 4-(-3)=7 days, Monday=1 → 4-1=3 days, etc. */
    var day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    var yearStart = new Date(Date.UTC(t.getUTCFullYear(),0,1));
    var weekNo = Math.ceil((((t - yearStart) / 86400000) + 1)/7);
    return t.getUTCFullYear() + '-W' + String(weekNo).padStart(2,'0');
  }
  /* YYYY-MM key for monthly Featured visit. */
  function thisMonthKey(){
    var d = new Date();
    return d.toISOString().slice(0, 7);
  }
  /* R166: Per-character per-DAY key for Legendary/Mythic blessings.
     History: started per-day, then briefly weekly to discourage farming,
     now back to daily with smaller bonuses (+1 legendary, +3 mythic)
     so daily check-ins on favorite high-tier heroes feel rewarding
     without minting too many hearts.
     Old hoh-blessing-[id]-[week] entries become orphans after this
     change; localStorage never reads them again so they age out
     naturally with no migration needed. */
  function blessingKey(charId){ return 'hoh-blessing-' + charId + '-' + todayYmd(); }
  /* R380 (2026-08-06, Jan's rule): the blessing belongs to the TIER, not to
     every character in it. The first mythic you open today pays 3, the first
     legendary pays 1, and the day is done. Per character it was a faucet that
     scaled with the size of the hall. Old per character keys just age out. */
  function blessingTierKey(tier){ return 'hoh-blessing-tier-' + tier + '-' + todayYmd(); }
  function hasTierBlessingToday(tier){
    try { return localStorage.getItem(blessingTierKey(tier)) === '1'; }
    catch(e){ return false; }
  }
  function recordTierBlessing(tier, bonus){
    try { localStorage.setItem(blessingTierKey(tier), '1'); } catch(e){}
    if(typeof window.hohAddHeartsToBank === 'function'){
      window.hohAddHeartsToBank(bonus, 'earn_ticket');
    }
  }
  /* The bonus tally also resets DAILY for the cap calculator (so the
     user's daily heart pool reads correctly). Now uses the same daily
     window as the per-character flag — both reset at midnight UTC. */
  function blessingBonusKey(){ return 'hoh-blessing-bonus-' + todayYmd(); }
  /* Featured visit flag. Per-character per-month so a featured hero
     who stays featured across months keeps granting +10 each month. */
  function featuredVisitKey(charId){ return 'hoh-featured-visit-' + charId + '-' + thisMonthKey(); }

  /* Sum of bonus hearts the user has earned via blessings today. The
     section's heartsCapToday() reads this and adds it to the daily
     pool. Resets at midnight UTC. */
  window.hohBlessingHeartsToday = function(){
    try { return parseInt(localStorage.getItem(blessingBonusKey()) || '0', 10) || 0; }
    catch(e){ return 0; }
  };

  function recordBlessing(charId, bonus){
    try { localStorage.setItem(blessingKey(charId), '1'); } catch(e){}
    /* Add the bonus directly to the persistent heart bank so it
       carries over between days like all other hearts. */
    if(typeof window.hohAddHeartsToBank === 'function'){
      window.hohAddHeartsToBank(bonus, 'earn_ticket');
    }
  }

  function hasBlessingToday(charId){
    try { return localStorage.getItem(blessingKey(charId)) === '1'; }
    catch(e){ return false; }
  }

  /* Spawn a centered floating banner over the detail modal that
     announces the blessing. Tier color matches Legendary gold or
     Mythic rainbow. Disappears in ~2.4s.
     R166: Cadence hint row.
       legendary / mythic → "Comes back tomorrow" (daily reset)
       featured           → "Refreshes next month"
     Surfacing the cadence inside the banner teaches the user the
     rhythm the first time they see it — no separate tutorial needed. */
  function showBlessingBanner(tier, bonus){
    /* R793: the live Hall shows no heart wallet, so it does not announce
       hearts landing in one. The grant itself is untouched (the
       Battlegrounds still spends hearts until its gold release). */
    if(r793Live()) return;
    var modal = document.querySelector('#hoh-modal .hoh-modal-content') || document.body;
    var rect = modal.getBoundingClientRect();
    var b = document.createElement('div');
    b.className = 'hoh-blessing-banner hoh-blessing-' + tier;
    b.style.left = (rect.left + rect.width / 2) + 'px';
    b.style.top = (rect.top + rect.height * 0.18) + 'px';
    var label = tier === 'mythic' ? "Mythic's Blessing"
      : tier === 'featured' ? "This Month's Featured"
      : "Legend's Blessing";
    var cadence = tier === 'featured' ? 'Refreshes next month' : 'Comes back tomorrow';
    b.innerHTML =
      '<span class="hoh-blessing-eyebrow">' + label + '</span>' +
      '<span class="hoh-blessing-bonus">Thanks for stopping by</span>' +
      '<span class="hoh-blessing-cadence">' + cadence + '</span>';
    document.body.appendChild(b);
    setTimeout(function(){ if(b.parentNode) b.parentNode.removeChild(b); }, 2400);
  }

  window.hohOnDetailOpen = function(c){
    if(!c) return;
    /* R224: the visit bonuses below (featured + Legendary/Mythic rarity blessing) and
       their banner popups are a signed-in heart feature. A logged-out visitor has no
       heart pool to grant into, so skip the grants and the popups entirely. */
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()) return;
    /* Featured visit: +5 hearts the first time the user opens the
       featured character per calendar month. Stays MONTHLY (the
       featured hero rotates monthly too — keying it to that rotation
       keeps it special). Independent of the rarity blessing so a
       Featured Mythic stacks both: +5 (featured) + 3 (mythic) = +8
       on the user's first daily open. */
    var firedFeatured = false;
    if(c.featured && !hasFeaturedVisitThisMonth(c.id)){
      recordFeaturedVisit(c.id, 5);
      if(typeof window.hohUpdateHeartCounter === 'function') window.hohUpdateHeartCounter();
      pulseHeartsBumped(5);
      showBlessingBanner('featured', 5);
      firedFeatured = true;
    }
    /* R166: Rarity blessing — Legendary (+1) and Mythic (+3), once
       per character per DAY. Smaller per-claim than the old weekly
       +3 / +5 but obtainable every day, so a player who returns
       daily and opens their favorite heroes accumulates a meaningful
       drip of hearts over time. The rarity banner gets a "Comes back
       tomorrow" subtitle so the user organically learns the cadence
       the first time they hit it (see showBlessingBanner). */
    /* R380: ONE blessing per tier per day. First mythic opened pays 3, first
       legendary pays 1. hasBlessingToday/recordBlessing (per character) are
       retired from this path; the tier latch is the only gate now. */
    var rar = (typeof window.hohGetRarity === 'function') ? window.hohGetRarity(c.hearts || 0) : { tier: 'common' };
    if((rar.tier === 'legendary' || rar.tier === 'mythic') && !hasTierBlessingToday(rar.tier)){
      var bonus = rar.tier === 'mythic' ? 3 : 1;
      recordTierBlessing(rar.tier, bonus);
      if(typeof window.hohUpdateHeartCounter === 'function') window.hohUpdateHeartCounter();
      /* Float +N bubble over the pill so the user sees the bonus arrive
         in their pool. Banner runs in parallel for tier-flavor copy. */
      pulseHeartsBumped(bonus);
      /* R166: Stagger the rarity banner if the featured banner just
         fired — both spawn at the same screen coordinates and would
         overlap on a Featured Mythic. 700ms lets the featured banner
         drift upward (by then it's around the 22%-30% point of its
         keyframe at translate(-50%,-55..-65%)) so the rarity banner
         lands cleanly below it. Both stay visible to the user. */
      if(firedFeatured){
        setTimeout(function(){ showBlessingBanner(rar.tier, bonus); }, 700);
      } else {
        showBlessingBanner(rar.tier, bonus);
      }
    }
  };

  function hasFeaturedVisitThisMonth(charId){
    try { return localStorage.getItem(featuredVisitKey(charId)) === '1'; }
    catch(e){ return false; }
  }
  function recordFeaturedVisit(charId, bonus){
    try { localStorage.setItem(featuredVisitKey(charId), '1'); } catch(e){}
    if(typeof window.hohAddHeartsToBank === 'function'){
      window.hohAddHeartsToBank(bonus, 'earn_ticket');
    }
  }

  /* ============================================================
     FEATURED HERO: REWARD PILL SWAP + WINNER NOTE + CROWN BONUS
     ============================================================
     The detail modal usually shows a ticket pill next to the heart.
     For the FEATURED character only, the pill becomes a rainbow
     CROWN. Clicking the crown grants +3 hearts (separate monthly
     localStorage key from the existing featured-visit +10 grant
     so the user can claim both). A winner note appears beneath
     the heart-side block stating the character won this month's
     free portrait and showing their win % at the time. */
  function featuredCrownKey(charId){ return 'hoh-featured-crown-' + charId + '-' + thisMonthKey(); }
  function hasCrownClaim(charId){
    try { return localStorage.getItem(featuredCrownKey(charId)) === '1'; }
    catch(e){ return false; }
  }
  function setCrownClaim(charId){
    try { localStorage.setItem(featuredCrownKey(charId), '1'); } catch(e){}
  }
  /* Compute the win % the featured character had at the time of
     winning. We don't store historical pool sizes, so we use their
     current ticket count divided by the current pool total — a fair
     approximation for "the odds they had". */
  function featuredWinPct(c){
    try {
      var mine = (c && c.tickets) || 0;
      if(typeof window.hohGetAllCharacters !== 'function') return 0;
      var all = window.hohGetAllCharacters() || [];
      var pool = 0;
      all.forEach(function(x){ pool += Math.max(1, (x && x.tickets) || 1); });
      if(pool <= 0) return 0;
      return Math.round((mine / pool) * 1000) / 10;
    } catch(e){ return 0; }
  }
  /* Public: returns either the regular ticket pill OR the rainbow
     crown pill. Called from the section's detail render. */
  window.hohRenderDetailRewardPill = function(c){
    if(c && c.featured){
      var claimed = hasCrownClaim(c.id);
      /* Crown is now a quiet circular icon — no "+3 hearts" label.
         The bonus action is unchanged (click grants 3 hearts once a
         month per featured char). R152: the tooltip now reads
         "Raffle winner" (was "Long live the king") — it appears on
         desktop hover and on mobile tap, and it states plainly what
         the crown means instead of leaving it as flavor. */
      return '<button type="button" class="hoh-tk hoh-tk-detail hoh-detail-crown' + (claimed ? ' hoh-detail-crown-claimed' : '') + '" id="hoh-detail-crown" aria-label="' + (claimed ? 'Crown bonus already claimed this month' : 'Tap the crown for a bonus') + '" onclick="hohClaimFeaturedCrown(\'' + c.id + '\',this)">' +
        '<svg class="hoh-detail-crown-svg" viewBox="0 0 24 18" width="20" height="16" aria-hidden="true">' +
          '<defs><linearGradient id="hohCrownGrad" x1="0%" y1="0%" x2="100%" y2="0%">' +
            '<stop offset="0%" stop-color="#ff5cf2"/><stop offset="20%" stop-color="#ffb347"/><stop offset="40%" stop-color="#ffe27a"/><stop offset="60%" stop-color="#7ed49a"/><stop offset="80%" stop-color="#7ed4ff"/><stop offset="100%" stop-color="#b794f4"/>' +
          '</linearGradient></defs>' +
          '<path d="M2 16 L4 5 L8 11 L12 3 L16 11 L20 5 L22 16 Z" fill="url(#hohCrownGrad)" stroke="#0a0a0f" stroke-width="0.6" stroke-linejoin="round"/>' +
          '<rect x="2" y="15" width="20" height="2" fill="url(#hohCrownGrad)" opacity="0.95"/>' +
          '<circle cx="4" cy="5" r="1.2" fill="#fff5d8"/><circle cx="12" cy="3" r="1.4" fill="#fff5d8"/><circle cx="20" cy="5" r="1.2" fill="#fff5d8"/>' +
        '</svg>' +
        '<span class="hoh-detail-crown-tip" aria-hidden="true">Raffle winner</span>' +
      '</button>';
    }
    /* R64: Default ticket pill — now also the apply-ticket button
       when (a) the breakdown tooltip is already open AND (b) the
       user has unspent tickets AND (c) they own the character. The
       central hohDetailTicketPillClick handler runs that gate; if
       it doesn\'t apply, it just toggles the breakdown open/closed
       like before.
       data-character-id is on the wrapper too so liveUpdateBreakdown
       can find it after a successful apply. */
    var charId = (c && c.id) || '';
    var safeCharId = String(charId).replace(/'/g, "\\'");
    /* R793: in the live Hall this pill only shows the count and its
       breakdown. The owner's daily ticket is the panel under this row. */
    var pillAria = r793Live() ? 'Raffle tickets. Tap for the breakdown.' : 'Raffle tickets, tap for breakdown — tap again with unspent tickets to apply one';
    return '<button type="button" class="hoh-tk hoh-tk-detail" id="hoh-detail-tk-pill" data-character-id="' + (charId ? String(charId).replace(/"/g, '&quot;') : '') + '" aria-label="' + pillAria + '" onclick="hohDetailTicketPillClick(this,event,\'' + safeCharId + '\')" onkeydown="if(event.key===\'Enter\'||event.key===\' \'){hohDetailTicketPillClick(this,event,\'' + safeCharId + '\');event.preventDefault();}"><span class="hoh-tk-icon"></span><span class="hoh-tk-count" id="hoh-detail-tk-count">' + ((c && c.tickets) || 1) + '</span><span class="hoh-detail-win-tip hoh-tk-tip" id="hoh-detail-tk-tip" data-character-id="' + (charId ? String(charId).replace(/"/g, '&quot;') : '') + '" aria-hidden="true"><div class="hoh-tk-bd-inner">' + (typeof window.hohBuildTicketBreakdownHtml === 'function' ? window.hohBuildTicketBreakdownHtml(c) : '') + '</div></span></button>';
  };

  /* R64: Detail-modal ticket pill click router. Two behaviors:
     1. If the breakdown tooltip is OPEN AND the user has unspent
        tickets AND owns this character, apply one ticket. This is
        the "click the badge to spend" path the user requested.
        The breakdown re-renders live with the bumped totals + new
        win %.
     2. Otherwise, toggle the breakdown open/closed (the legacy
        behavior). */
  window.hohDetailTicketPillClick = function(pillEl, ev, charId){
    if(ev){ try { ev.stopPropagation(); ev.preventDefault(); } catch(_){} }
    if(!pillEl) return;
    var isOpen = pillEl.classList.contains('hoh-tk-tip-open');
    var unspentNow = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
    /* R687: any actively collected card can receive a raffle ticket;
       the server separately decides whether it is also a submitted
       hero and therefore earns the level. */
    var ownsIt = ticketOwnsActiveCard(charId);
    if(isOpen && unspentNow > 0 && ownsIt){
      /* Apply one ticket. The breakdown stays open and live-updates. */
      if(typeof window.hohApplyTicket === 'function'){
        window.hohApplyTicket(charId, { targetEl: pillEl, silent: false });
      }
      return;
    }
    /* Default toggle behavior. */
    if(typeof window.hohToggleTicketTip === 'function'){
      window.hohToggleTicketTip(pillEl, ev);
    }
  };
  /* Winner note rendered beneath the heart-side block. Only for
     featured characters. Shows the win % they had. */
  window.hohRenderDetailWinnerNote = function(c){
    if(!c || !c.featured) return '';
    var pct = featuredWinPct(c);
    var pctTxt = pct < 1 ? pct.toFixed(2) : pct.toFixed(1);
    /* Dynamic month label — winners are drawn on the 1st for the
       month that just ENDED, so we display the PREVIOUS calendar
       month's name (e.g. "April's Winner" throughout May). The
       date.setDate(1) call before shifting months avoids month-end
       overflow (Mar 31 → Feb 31 → Mar 3 bug). */
    var winnerMonth = 'Month';
    try {
      var d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - 1);
      winnerMonth = d.toLocaleString('en-US', { month:'long' });
    } catch(_){}
    return '<div class="hoh-detail-winner-note" role="note">' +
      '<span class="hoh-detail-winner-eyebrow">' + winnerMonth + '’s Winner</span>' +
      '<span class="hoh-detail-winner-line">Won a free portrait from the Hall of Heroes raffle with a <strong>' + pctTxt + '%</strong> chance to win.</span>' +
    '</div>';
  };
  /* Click handler for the crown — grants +3 hearts (once per
     featured character per month) and animates them to the pill.
     Also flashes the "Despite all odds" tooltip on every tap so
     mobile users (where there's no hover) see the label. */
  window.hohClaimFeaturedCrown = function(charId, btn){
    if(!charId) return;
    /* Flash the tooltip for ~1.6s on every interaction. Desktop hover
       already shows it via :hover; this branch handles mobile taps. */
    if(btn){
      btn.classList.add('hoh-detail-crown-tip-show');
      setTimeout(function(){ btn.classList.remove('hoh-detail-crown-tip-show'); }, 1600);
    }
    if(hasCrownClaim(charId)){
      /* Already claimed — quick wiggle. */
      if(btn){
        btn.classList.add('hoh-detail-crown-shake');
        setTimeout(function(){ btn.classList.remove('hoh-detail-crown-shake'); }, 450);
      }
      return;
    }
    setCrownClaim(charId);
    if(typeof window.hohAddHeartsToBank === 'function') window.hohAddHeartsToBank(3, 'earn_task');
    if(typeof window.hohUpdateHeartCounter === 'function') window.hohUpdateHeartCounter();
    if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast('A crown for the crowned', 'Claimed for this month.', true);
    /* Visual: the 3 hearts pop at the crown (Jan, 2026-09-27: nothing flies
       to the wallet pill any more; see hohHeartBurst in hoh-wallet.js). */
    try {
      if(btn && typeof window.hohHeartBurst === 'function') window.hohHeartBurst(btn, { n: 3 });
    } catch(_){}
    if(btn){
      btn.classList.add('hoh-detail-crown-claimed');
      btn.setAttribute('aria-label', 'Crown bonus already claimed this month');
    }
  };

  /* Mythic celebration: spawned when a character first crosses the
     hidden 300-heart threshold. Banner with rainbow eyebrow + name +
     "+1 raffle ticket" line, plus a confetti burst from the card. */
  window.hohMythicCelebrate = function(target, c){
    try { if(window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches) return; } catch(e){}
    if(!target) return;
    var rect = target.getBoundingClientRect();
    var banner = document.createElement('div');
    banner.className = 'hoh-mythic-banner';
    banner.style.left = (rect.left + rect.width/2) + 'px';
    banner.style.top = (rect.top + rect.height/2) + 'px';
    var name = (c && c.name) ? String(c.name).replace(/[<>&"']/g, function(ch){
      return { '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;' }[ch];
    }) : 'this hero';
    banner.innerHTML =
      '<span class="hoh-mythic-banner-eyebrow">Mythic Unlocked</span>' +
      '<span class="hoh-mythic-banner-name">' + name + '</span>' +
      '<span class="hoh-mythic-banner-bonus">+1 raffle ticket</span>';
    document.body.appendChild(banner);
    setTimeout(function(){ if(banner.parentNode) banner.parentNode.removeChild(banner); }, 2400);
    try {
      if(typeof confetti === 'function'){
        confetti({
          particleCount: 160, spread: 90, startVelocity: 45, gravity: 0.5,
          origin: { x: (rect.left + rect.width/2) / window.innerWidth, y: (rect.top + rect.height/2) / window.innerHeight },
          colors: ['#ff5cf2','#ffb347','#7ed4ff','#b794f4','#7ed49a','#fff5d8'],
          zIndex: 999999
        });
      }
    } catch(e){}
  };

  /* Burst animation: spawns a quick puff of the ticket icon at the
     header pill's position so the user feels the reward. */
  function celebrateTicketEarned(){
    var pill = document.getElementById('hoh-tk-header');
    if(!pill) return;
    var rect = pill.getBoundingClientRect();
    var burst = document.createElement('span');
    burst.className = 'hoh-tk-burst';
    burst.style.cssText =
      'position:fixed;left:' + (rect.left + rect.width/2) + 'px;' +
      'top:' + (rect.top + rect.height/2) + 'px;' +
      'transform:translate(-50%,-50%);pointer-events:none;z-index:99998;';
    burst.innerHTML =
      '<span class="hoh-tk-icon" aria-hidden="true" style="width:28px;height:28px;display:block;"></span>' +
      '<span class="hoh-tk-burst-label">+1 ticket</span>';
    document.body.appendChild(burst);
    setTimeout(function(){ if(burst.parentNode) burst.parentNode.removeChild(burst); }, 1400);
    /* Pulse the pill itself so it draws the eye even after the burst. */
    pill.classList.remove('hoh-tk-pulse');
    void pill.offsetWidth;
    pill.classList.add('hoh-tk-pulse');
    /* If this ticket pushed the user to 4/4, fire the daily completion
       reward (large confetti + 20 hearts + toast). Date-gated. */
    maybeFireAllTicketsReward();
  }

  /* Daily "all 4 tickets" completion reward. Fires once per day when
     computeDailyTotal().total reaches 4. Order-independent — works no
     matter which quest the user finishes last. Date-keyed localStorage
     gate prevents re-fires across page reloads. */
  function allTicketsRewardKey(){ return 'hoh-all-tickets-reward-' + new Date().toISOString().slice(0,10); }
  function allTicketsRewardClaimed(){
    try { return localStorage.getItem(allTicketsRewardKey()) === '1'; }
    catch(e){ return false; }
  }
  function maybeFireAllTicketsReward(){
    if(allTicketsRewardClaimed()) return;
    var d = computeDailyTotal();
    if(!d || d.total < d.max) return;
    try { localStorage.setItem(allTicketsRewardKey(), '1'); } catch(e){}
    /* Grant +20 hearts to the spendable bank. */
    if(typeof window.hohAddHeartsToBank === 'function') window.hohAddHeartsToBank(20, 'earn_ticket');
    /* Large two-wave confetti burst. First wave from screen center
       (where the user's eye is on the ticket pill or modal), second
       wave from the bottom-right pill location 280ms later for a
       "the pill is celebrating" feel. zIndex 999999 so it floats
       above all modals. */
    try {
      if(typeof window.confetti === 'function'){
        window.confetti({
          particleCount: 180, spread: 110, startVelocity: 48,
          origin: { x: 0.5, y: 0.45 },
          colors: ['#f2d98e','#ed97fe','#a4d8ff','#7ed49a','#fff5d8','#ffb347','#b794f4'],
          zIndex: 999999
        });
        var pill = document.getElementById('hoh-tk-header');
        var px = 0.92, py = 0.92;
        if(pill){
          var pr = pill.getBoundingClientRect();
          px = (pr.left + pr.width / 2) / window.innerWidth;
          py = (pr.top + pr.height / 2) / window.innerHeight;
        }
        setTimeout(function(){
          try {
            window.confetti({
              particleCount: 90, spread: 80, startVelocity: 36,
              origin: { x: px, y: py },
              colors: ['#f2d98e','#ed97fe','#fff5d8'],
              zIndex: 999999
            });
          } catch(_){}
        }, 280);
      }
    } catch(_){}
    if(typeof window.hohShowHeartToast === 'function'){
      window.hohShowHeartToast('All 4 tickets earned', 'The Hall thanks you.');
    }
  }
  /* Expose so external callers (the Jangela debug, future quest
     hooks) can manually re-check after they bump ticket flags. */
  window.hohMaybeFireAllTicketsReward = maybeFireAllTicketsReward;

  /* Edit cooldown pre-check used by hohOpenEditForm. Calls the
     can_edit_character RPC and returns a friendly result the section
     can render directly into a toast.

     Returns:
       null                            -> not signed in / no DB, allow
       { allowed: true }               -> allow opening the form
       { allowed: false, title, sub }  -> show toast with these strings
  */
  window.hohCheckEditCooldown = async function(characterId){
    var sb = window.hohSupabaseClient;
    var em = signedInEmail();
    var pin = readPin();
    if(!sb || !em || !pin || !characterId) return null;
    try {
      var res = await sb.rpc('can_edit_character', {
        character_id: characterId, user_email: em, user_pin: pin
      });
      if(!res || !res.data) return { allowed: true };
      if(res.data.allowed) return { allowed: true };

      var reason = res.data.reason || '';
      /* HALL-GOOGLE-01: a Google sign-in ('g1.' session) where the live can_edit_character is still PIN only
         (HALL-GOOGLE-01-VERIFY reads PIN ONLY there) answers 'unauthenticated'. The save itself is not PIN-checked
         and the cooldown trigger still holds, so the edit opens as it would have. */
      if(reason === 'unauthenticated' && /^g1\./.test(String(pin))) return { allowed: true };
      var retryAt = res.data.retry_at ? new Date(res.data.retry_at) : null;
      var retryStr = '';
      if(retryAt && !isNaN(retryAt.getTime())){
        var ms = retryAt.getTime() - Date.now();
        if(ms > 0){
          var hrs = Math.ceil(ms / 3600000);
          retryStr = (hrs >= 24)
            ? Math.ceil(hrs / 24) + ' day' + (hrs >= 48 ? 's' : '')
            : hrs + ' hour' + (hrs === 1 ? '' : 's');
        }
      }
      var title = 'Edit limit hit';
      var sub = retryStr ? 'Available again in ' + retryStr + '.' : 'Try again later.';
      if(reason === 'character_cooldown'){
        title = 'This hero was just edited';
        sub = 'Each hero can be edited once a day.' + (retryStr ? ' Available again in ' + retryStr + '.' : '');
      } else if(reason === 'user_cooldown'){
        title = 'You\'re editing too often';
        sub = 'One edit per 24 hours across all your heroes.' + (retryStr ? ' Available again in ' + retryStr + '.' : '');
      } else if(reason === 'not_owner'){
        title = 'Not your hero';
        sub = 'Sign in with the email used to submit this character.';
      } else if(reason === 'unauthenticated'){
        title = 'Session expired';
        sub = 'Open Your Hall and sign in again.';
      }
      return { allowed: false, title: title, sub: sub };
    } catch(e){
      console.warn('[edit cooldown]', e);
      return { allowed: true }; /* Fail-open; trigger still enforces 7d. */
    }
  };

  /* Refresh the cache from get_my_raffle_state. Called by Raffle and
     Roll Call modules when they open. Returns a Promise. */
  window.hohFetchRaffleState = async function(){
    var sb = window.hohSupabaseClient;
    var em = signedInEmail();
    var pin = readPin();
    if(!sb || !em || !pin) return null;
    try {
      var res = await sb.rpc('get_my_raffle_state', { user_email: em, user_pin: pin });
      if(res && res.data && !res.error){
        /* R410 (Jan): "god mode should have the same experience as normal
           people". R82 used to snapshot god's daily flags before this merge and
           restore them after, so the server could never downgrade
           submitted_today, tinder_today, bounty_solved or the ticket counts.
           That existed to prop up the old fake-4/4 debug workflow, and the
           price was that god's daily state was a fiction: the tasks read as
           done when the server said they were not. The server is the single
           source of truth for everyone now, god included. To see 4 of 4, do
           the four things. */
        window.hohTicketState = Object.assign(window.hohTicketState, res.data);
        renderHeaderCounter();
        /* R141-C: pull the server's authoritative daily-claim list
           every time we refresh the raffle state. The server is the
           single source of truth for "what has this account claimed
           today" — pulling it here ensures device 2 immediately sees
           wander_20 / wander_40 as already-claimed if device 1
           already banked them, AND that re-claim attempts on
           device 2 hit the now-locked local flag before the
           additional server gate even fires. Best-effort: if the RPC
           is missing or auth fails, we degrade silently (the picker
           and quest UI still work, just without cross-device sync). */
        try {
          if(typeof window.hohServerSyncDailyClaims === 'function'){
            window.hohServerSyncDailyClaims();
          }
        } catch(_){}
        /* Notify other surfaces that the ticket state just got
           refreshed from the server. Daily Tickets modal, header
           tooltip, missing-today list — all subscribe to this and
           re-render with fresh data. Without this dispatch the
           modal would stay on whatever cached state it last rendered
           with, then update only when manually re-opened (the open
           handler triggers another fetch). The user reported this
           as "header shows 2/4, modal opens at 4/4" — both surfaces
           now read the same authoritative state immediately after
           the page-load fetch. */
        try {
          document.dispatchEvent(new CustomEvent('hoh:ticket-state-changed', {
            detail: { source: 'fetchRaffleState' }
          }));
        } catch(_){}
        /* R140-Z: Bounty feature removed. The bounty_solved field on
           the raffle state response is ignored client-side. */
        return res.data;
      }
    } catch(e){ console.warn('[Tickets] state fetch', e); }
    return null;
  };

  /* Expose the header-counter renderer globally so other files
     (hoh-section-core, etc.) can trigger an immediate refresh of
     the visible counter pill whenever the heart bank changes. Without
     this, hidden-heart claims and other bank-modifying actions would
     update localStorage but the visible pill would stay stale until
     the next ticket roundtrip or page reload. */
  window.hohRenderHeaderCounter = renderHeaderCounter;

  /* R148-Z41: Wake the wallet pill the moment the user signs in via
     hero submission OR the Your Hall sign-in form. The renderer
     gates on hohIsHallSignedIn(), so before this event fires the
     pill never exists. After, it pops in next animation frame. */
  try {
    document.addEventListener('hoh:signed-in', function(){
      try { renderHeaderCounter(); } catch(_){}
    });
  } catch(_){}

  /* R148-Z26 Round 4: Periodic Oath check — re-render the header once
     per hour while the page is open, so a 5-day Oath that becomes
     claimable mid-session surfaces its Claim button without requiring
     a manual page refresh. Cheap: renderHeaderCounter is DOM-cached. */
  try {
    setInterval(function(){
      try {
        if(typeof window.hohInventoryHas === 'function' && window.hohInventoryHas('patronsOath')){
          renderHeaderCounter();
        }
      } catch(_){}
    }, 60 * 60 * 1000);
  } catch(_){}

  /* Initial render after the page settles. The header pill won't exist
     until the section has rendered its header markup, so we retry. */
  function ensureRendered(){
    renderHeaderCounter();
    var pill = document.getElementById('hoh-tk-header');
    if(!pill){ setTimeout(ensureRendered, 250); return; }
    /* R127-A: Seed the wallet-credit ledger BEFORE any reconcile or
       fetch fires. This avoids retroactively topping up users whose
       wallet predates this fix — the ledger starts pre-populated for
       anything already claimed today, so reconcile only credits NEW
       claims that genuinely missed the wallet write. */
    try { seedWalletLedgerFromClaims(); } catch(_){}
    if(signedInEmail() && readPin()) window.hohFetchRaffleState();
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', ensureRendered);
  } else {
    ensureRendered();
  }
  /* Force a final re-render once all subresources have loaded — this
     catches races where the daily heart grant was deferred (e.g., the
     sign-in check ran before the token was hydrated, or a delayed
     wallet bump from another module). The function is idempotent so
     calling it twice is harmless. */
  window.addEventListener('load', function(){
    setTimeout(function(){
      try { renderHeaderCounter(); } catch(_){}
    }, 200);
  });
  /* R61: Real-time pill updates. Without these, the user could claim
     a wander milestone (or solve a bounty / submit a hero) and the
     pill would only repaint on the next page interaction. The
     ticket-state-changed event is dispatched by every claim path
     (wander milestones, submission, bounty, RPC refresh); ff-quest-
     progress fires whenever a heart bumps the wander count so the
     tooltip "Explore the Hall +1" copy updates as the user passes
     each threshold. Keep handlers idempotent. */
  document.addEventListener('hoh:ticket-state-changed', function(){
    try { renderHeaderCounter(); } catch(_){}
    /* R127-A: Run a reconcile pass on every state change. If a claim
       just landed but the wallet credit never fired (server clobbered
       tickets_today, etc.), the ledger will spot the gap and backfill. */
    try { reconcileWalletFromClaims(); } catch(_){}
  });
  document.addEventListener('hoh:ff-quest-progress', function(){
    try { renderHeaderCounter(); } catch(_){}
  });
  document.addEventListener('hoh:wander-quest-complete', function(){
    try { renderHeaderCounter(); } catch(_){}
  });
  /* R66: Wallet changes (earn → unspent, apply → unspent--) repaint
     the sticky pill so the new "to spend" row shows / hides / counts
     correctly without any user interaction. */
  document.addEventListener('hoh:unspent-tickets-changed', function(){
    try { renderHeaderCounter(); } catch(_){}
  });
  document.addEventListener('hoh:ticket-applied', function(){
    try { renderHeaderCounter(); } catch(_){}
  });
  /* R61: Daily-rollover guard. The in-memory window.hohTicketState
     can carry stale yesterday flags (tinder_today, submitted_today,
     bounty_solved) into a new day if the user keeps the tab open
     overnight. Server flags refresh on the next RPC, but in the
     meantime the pill could read "complete" with stale data. We
     stamp the day on every render and zero the volatile flags when
     the day rolls over so the pill goes back to 0/4 until the
     server confirms otherwise. */
  var lastRenderYmd = '';
  function ymdNow(){
    try { return new Date().toISOString().slice(0,10); } catch(_){ return ''; }
  }
  var origRender = renderHeaderCounter;
  renderHeaderCounter = function(){
    var today = ymdNow();
    if(lastRenderYmd && today && lastRenderYmd !== today){
      try {
        var s = window.hohTicketState || {};
        s.tickets_today = 0;
        s.submitted_today = false;
        s.bounty_solved = false;
        s.tinder_today = false;
        s.roll_call_completed = false;
        if(typeof window.hohFetchRaffleState === 'function'){
          try { window.hohFetchRaffleState(); } catch(_){}
        }
      } catch(_){}
    }
    lastRenderYmd = today;
    origRender();
  };
  window.hohRenderHeaderCounter = renderHeaderCounter;

  /* Re-render header on visibility change (browser tab refocus). The
     daily counter might be stale if the user left the tab open across
     midnight UTC. */
  document.addEventListener('visibilitychange', function(){
    if(!document.hidden && signedInEmail() && readPin()){
      window.hohFetchRaffleState();
    }
  });

  /* ============ DELAYED ARCHETYPE QUIZ REVEAL ============
     The global quest popup (.ff-qf) is hidden by default on Hall of
     Heroes via CSS. We reveal it after 3 minutes on the page OR
     immediately when the user completes a quest (submitted today,
     bounty solved, or hero's circle done). The user can still
     dismiss it normally; once revealed it stays revealed for the
     session. */
  var QUEST_POPUP_DELAY_MS = 3 * 60 * 1000;
  var questPopupShown = false;
  function revealQuestPopup(){
    if(questPopupShown) return;
    var el = document.querySelector('.ff-qf');
    if(!el) return;
    questPopupShown = true;
    /* Snippet ships with inline style="display:none". Clear that so
       the CSS opacity:0 baseline can take effect, then on the next
       frame add the .hoh-qf-shown class to fade it in (instead of
       snapping). The double rAF ensures the browser registers the
       opacity:0 starting state before the transition target lands. */
    el.style.display = '';
    requestAnimationFrame(function(){
      requestAnimationFrame(function(){
        el.classList.add('hoh-qf-shown');
      });
    });
  }
  /* Time-based: 3 minutes on page. */
  setTimeout(revealQuestPopup, QUEST_POPUP_DELAY_MS);
  /* Quest-completion based: any of the 3 daily quests being marked
     done flips the reveal early. Polls hohTicketState since it's
     mutated externally by the heart/bounty/tinder flows. */
  var questCheckTimer = setInterval(function(){
    if(document.hidden) return; /* PERF-6: quests cannot complete in a hidden tab */
    var st = window.hohTicketState || {};
    var d = computeDailyTotal();
    if(d && (d.submitted || d.tinder || d.bounty)){
      revealQuestPopup();
      clearInterval(questCheckTimer);
    }
  }, 4000);

  /* ============================================================
     TRIPLE-TAP HERO BANNER EASTER EGG
     ============================================================
     Each tap on the hero banner image glows a different color and
     scales subtly. The third tap fires a satisfying burst and grants
     +3 hearts that fly visibly into the bottom-right ticket pill so
     the user sees where the reward landed. After the burst the
     banner becomes unclickable for the rest of the session and
     reverts to its original look. Daily grant is also localStorage-
     gated so refreshing the page won't re-trigger. */
  var TAP_COLORS = ['#a4d8ff', '#ed97fe', '#f2d98e'];
  var tapCount = 0;
  var tapResetTimer = null;
  var tapBurstFired = false;
  function tapKey(){ return 'hoh-banner-tap-' + new Date().toISOString().slice(0,10); }
  function tapGrantedToday(){ try { return localStorage.getItem(tapKey()) === '1'; } catch(e){ return false; } }
  function setTapGrantedToday(){ try { localStorage.setItem(tapKey(), '1'); } catch(e){} }
  function clearTapClasses(banner){
    banner.classList.remove('hoh-hall-banner-tap-1','hoh-hall-banner-tap-2','hoh-hall-banner-tap-3','hoh-hall-banner-burst');
  }
  /* The banner's hearts pop at its centre, where they were found. Jan,
     2026-09-27: they used to fly to the ticket pill, which the live Hall
     hides, so they landed in the top-left corner. The name stays for its
     caller. */
  function flyHeartsToPill(banner, n){
    if(!banner || !n || typeof window.hohHeartBurst !== 'function') return;
    window.hohHeartBurst(banner, { n: n });
  }
  function attachBannerTap(){
    var banner = document.querySelector('.hoh-hall-banner-hero');
    if(!banner || banner.dataset.hohTapBound) return;
    banner.dataset.hohTapBound = '1';
    /* If the daily was already claimed in a prior session, lock it
       immediately — no glow, no cursor. */
    if(tapGrantedToday()){
      banner.style.cursor = '';
      banner.style.pointerEvents = 'none';
      tapBurstFired = true;
      return;
    }
    banner.style.cursor = 'pointer';
    banner.addEventListener('click', function(e){
      if(tapBurstFired){ return; }
      tapCount++;
      /* Per-tap visible glow/scale effects intentionally REMOVED.
         The user found the stacking glow distracting on excessive
         clicks — the banner now reads as a static image until the
         third tap fires the celebratory burst. The taps are still
         counted silently so the +3 hearts reward still triggers on
         tap 3. After the burst the banner becomes inert for the
         rest of the day. */
      if(tapCount >= 3){
        /* BURST + grant. */
        banner.classList.add('hoh-hall-banner-burst');
        setTapGrantedToday();
        tapBurstFired = true;
        tapCount = 0;
        if(typeof window.hohAddHeartsToBank === 'function') window.hohAddHeartsToBank(3, 'earn_task');
        if(typeof window.hohUpdateHeartCounter === 'function') window.hohUpdateHeartCounter();
        if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast('You found it', 'The Hall whispers thanks.', true);
        try {
          if(typeof window.confetti === 'function'){
            var rect = banner.getBoundingClientRect();
            window.confetti({
              particleCount: 80, spread: 70, startVelocity: 35,
              origin: { x: (rect.left + rect.width/2) / window.innerWidth, y: (rect.top + rect.height/2) / window.innerHeight },
              colors: ['#ed97fe','#f2d98e','#a4d8ff','#7ed49a','#fff5d8'],
              zIndex: 999999
            });
          }
        } catch(_){}
        /* Stagger 3 hearts flying to the bottom-right pill. */
        flyHeartsToPill(banner, 3);
        /* Revert visual state and disable further clicks for the
           rest of the session. */
        setTimeout(function(){
          clearTapClasses(banner);
          banner.style.cursor = '';
          banner.style.pointerEvents = 'none';
        }, 1400);
      } else {
        if(tapResetTimer) clearTimeout(tapResetTimer);
        tapResetTimer = setTimeout(function(){
          tapCount = 0;
          clearTapClasses(banner);
        }, 2200);
      }
    });
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', function(){ setTimeout(attachBannerTap, 200); });
  } else {
    setTimeout(attachBannerTap, 200);
  }
})();

/* ============================================================
   R793 · TODAY'S TICKET, GIVEN ON YOUR OWN HERO'S PAGE
   ============================================================
   Jan, 2026-09-22: "tickets only to your own heroes, one per day per own
   hero, applied only in the main Hall when visiting your own hero."

   The panel sits under the stat row of your own hero's detail view (and a
   compact version under the card in the card view). One button, "Give
   today's ticket". The door (hoh_hall_ticket_apply) adds exactly one
   ticket and answers with the hero's new count, the draw's pool and the
   hero's chance. The panel then plays the moment: the ticket flies into
   the box, the box takes it, and the chance counts up from the old number
   to the new one with a small rising "+". After that it reads "Given
   today" with the time the next ticket opens (midnight Pacific, in the
   viewer's own clock). Reduced motion shows the new chance and a still
   highlight on the same beat, with nothing flying.

   Every number shown is the door's. Before today's state arrives the
   chance reads as a quiet placeholder rather than a browser estimate.
   Anyone else's hero, a signed-out visitor, or a Hall where the door is
   not installed yet (legacy mode) gets no panel at all. */
(function(){
  'use strict';
  if(typeof window === 'undefined') return;
  function D(){ return window.HOHHallDoors || null; }
  function live(){ try { return (typeof window.hohR793Live === 'function') && window.hohR793Live(); } catch(_){ return false; } }
  function esc(s){ return String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;'); }
  /* an id inside a single-quoted JS string inside an HTML attribute: escape
     for JS first, then for HTML (the parser undoes only the second) */
  function jsq(s){ return String(s == null ? '' : s).replace(/\\/g,'\\\\').replace(/'/g,"\\'").replace(/[\r\n\u2028\u2029]/g,' ').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
  function reduced(){ try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch(_){ return false; } }
  function ownHero(c){
    if(!c || !c.id) return false;
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()) return false;
    /* R793.1 fix: the ticket door's own owner rule (auth_email when it is
       set, else the form email), the same one the Hall's own-first order
       uses, so the panel shows exactly where the door will say yes */
    try {
      if(typeof window.hohIsHallDoorOwner === 'function') return !!window.hohIsHallDoorOwner(c);
      return (typeof window.hohIsCharacterOwner === 'function') && window.hohIsCharacterOwner(c);
    } catch(_){ return false; }
  }
  function findHero(id){
    try {
      var all = (typeof window.hohGetAllCharactersFull === 'function') ? window.hohGetAllCharactersFull() : ((typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : []);
      for(var i = 0; i < (all || []).length; i++){ if(all[i] && String(all[i].id) === String(id)) return all[i]; }
    } catch(_){}
    return null;
  }
  /* The box: a small gold coffer with a slot, drawn here (house rule: our
     own icons, no packs, nothing generated). */
  var BOX_SVG =
    '<svg class="hoh-tkgive-svg" viewBox="0 0 32 32" width="30" height="30" aria-hidden="true" focusable="false">' +
      '<path class="hoh-tkgive-lid" d="M5.5 11.5h21l-2.2-5.2H7.7z" />' +
      '<rect class="hoh-tkgive-body" x="5.5" y="11.5" width="21" height="15.5" rx="2.2" />' +
      '<path class="hoh-tkgive-band" d="M5.5 17.4h21" />' +
      '<path class="hoh-tkgive-slot" d="M12.2 8.9h7.6" />' +
    '</svg>';

  /* Markup. The number starts as a placeholder; mount() fills it from the door. */
  window.hohHallTicketPanelHtml = function(c, variant){
    if(!live() || !ownHero(c)) return '';
    variant = (variant === 'card') ? 'card' : 'detail';
    var id = esc(c.id);
    var name = esc(c.name || 'your hero');
    if(variant === 'card'){
      return '<div class="hoh-tkgive hoh-tkgive-card is-loading" data-tkgive-id="' + id + '" data-tkgive-variant="card">' +
        '<span class="hoh-tkgive-box" aria-hidden="true">' + BOX_SVG + '<span class="hoh-tkgive-sparks"></span></span>' +
        '<span class="hoh-tkgive-main">' +
          '<span class="hoh-tkgive-chance"><span class="hoh-tkgive-num" data-tkgive-num>&hellip;</span><span class="hoh-tkgive-rise" data-tkgive-rise aria-hidden="true"></span></span>' +
          '<span class="hoh-tkgive-label">chance to win</span>' +
        '</span>' +
        '<span class="hoh-tkgive-act" data-tkgive-act>' +
          '<button type="button" class="hoh-tkgive-btn" data-tkgive-btn onclick="event.stopPropagation();hohHallGiveTicket(\'' + jsq(c.id) + '\',this)" aria-label="Give today\'s ticket to ' + name + '">Give today&rsquo;s ticket</button>' +
        '</span>' +
        '<span class="hoh-tkgive-sub" data-tkgive-sub></span>' +
        '<span class="hoh-hall-visually-hidden" data-tkgive-live aria-live="polite"></span>' +
      '</div>';
    }
    return '<section class="hoh-tkgive hoh-tkgive-detail is-loading" data-tkgive-id="' + id + '" data-tkgive-variant="detail" aria-label="Today\'s raffle ticket for ' + name + '">' +
      '<span class="hoh-tkgive-box" aria-hidden="true">' + BOX_SVG + '<span class="hoh-tkgive-sparks"></span></span>' +
      '<div class="hoh-tkgive-main">' +
        '<div class="hoh-tkgive-label">Chance to win this month</div>' +
        '<div class="hoh-tkgive-chance"><span class="hoh-tkgive-num" data-tkgive-num>&hellip;</span><span class="hoh-tkgive-rise" data-tkgive-rise aria-hidden="true"></span></div>' +
        '<div class="hoh-tkgive-sub" data-tkgive-sub>Each of your heroes takes one ticket a day.</div>' +
      '</div>' +
      '<div class="hoh-tkgive-act" data-tkgive-act>' +
        '<button type="button" class="hoh-tkgive-btn" data-tkgive-btn onclick="event.stopPropagation();hohHallGiveTicket(\'' + jsq(c.id) + '\',this)">Give today&rsquo;s ticket</button>' +
      '</div>' +
      '<p class="hoh-hall-visually-hidden" data-tkgive-live aria-live="polite"></p>' +
    '</section>';
  };

  function panelsFor(id){
    var out = [];
    var nodes = document.querySelectorAll('.hoh-tkgive[data-tkgive-id]');
    for(var i = 0; i < nodes.length; i++){ if(nodes[i].getAttribute('data-tkgive-id') === String(id)) out.push(nodes[i]); }
    return out;
  }
  function chanceOf(id){
    var d = D(); if(!d) return null;
    var st = d.tickets.cached(); if(!st) return null;
    var h = st.heroes[String(id)]; if(!h || !st.pool) return null;
    return { chance: h.tickets / st.pool, tickets: h.tickets, applied: !!h.applied_today, next_at: st.next_at };
  }
  function doneHtml(){
    return '<span class="hoh-tkgive-done"><span class="hoh-tkgive-check" aria-hidden="true"></span>Given today</span>';
  }
  function paintPanel(el, info, opts){
    opts = opts || {};
    if(!el) return;
    var num = el.querySelector('[data-tkgive-num]');
    var sub = el.querySelector('[data-tkgive-sub]');
    var act = el.querySelector('[data-tkgive-act]');
    var d = D();
    if(info){
      el.classList.remove('is-loading');
      if(num && !opts.keepNumber) num.textContent = d ? d.formatChance(info.chance) : '';
      if(info.applied){
        el.classList.add('is-given');
        if(act && !act.querySelector('.hoh-tkgive-done')) act.innerHTML = doneHtml();
        if(sub) sub.textContent = (info.tickets === 1 ? '1 ticket. ' : (info.tickets + ' tickets. ')) + (d ? d.comeBackText(info.next_at) : 'Come back tomorrow.');
      } else {
        el.classList.remove('is-given');
        if(sub) sub.textContent = (info.tickets === 1 ? '1 ticket so far. ' : (info.tickets + ' tickets so far. ')) + 'Each of your heroes takes one a day.';
      }
    }
  }
  /* Fill any panel inside `root` from today's state (one door call, cached). */
  window.hohHallTicketPanelMount = function(root){
    var d = D(); if(!d || !live()) return;
    var panels = (root || document).querySelectorAll('.hoh-tkgive[data-tkgive-id]');
    if(!panels.length) return;
    var paintAll = function(){
      for(var i = 0; i < panels.length; i++){
        var id = panels[i].getAttribute('data-tkgive-id');
        var info = chanceOf(id);
        if(info) paintPanel(panels[i], info);
        else if(panels[i].classList.contains('is-loading') && d.tickets.cached()){
          /* the door does not list this hero as yours: nothing to give */
          panels[i].classList.remove('is-loading');
          panels[i].classList.add('is-hidden');
        }
      }
    };
    paintAll();
    d.tickets.state().then(function(st){
      if(st === null && d.mode() === 'legacy'){
        for(var i = 0; i < panels.length; i++) panels[i].classList.add('is-hidden');
        return;
      }
      paintAll();
    });
  };

  /* ---------- the moment ---------- */
  function easeOut(t){ return 1 - Math.pow(1 - t, 3); }
  function countUp(num, from, to, ms){
    var d = D(); if(!num || !d) return;
    var dec = d.chanceDecimals(from, to);
    var t0 = null;
    function step(ts){
      if(t0 === null) t0 = ts;
      var k = Math.min(1, (ts - t0) / ms);
      num.textContent = d.formatChance(from + (to - from) * easeOut(k), dec);
      if(k < 1) requestAnimationFrame(step);
      else num.textContent = d.formatChance(to, dec);
    }
    requestAnimationFrame(step);
  }
  function flyTicket(fromEl, toEl, ms){
    return new Promise(function(resolve){
      try {
        var a = fromEl.getBoundingClientRect(), b = toEl.getBoundingClientRect();
        var t = document.createElement('span');
        t.className = 'hoh-tk-icon hoh-tkgive-flyer';
        t.setAttribute('aria-hidden', 'true');
        var sx = a.left + a.width / 2, sy = a.top + a.height / 2;
        var ex = b.left + b.width / 2, ey = b.top + b.height * 0.28;
        t.style.left = (sx - 11) + 'px'; t.style.top = (sy - 8) + 'px';
        document.body.appendChild(t);
        var dx = ex - sx, dy = ey - sy;
        var lift = Math.min(-44, dy - 60);
        var anim = t.animate([
          { transform: 'translate(0,0) rotate(-12deg) scale(1.15)', opacity: 0.2 },
          { transform: 'translate(' + (dx * 0.45) + 'px,' + lift + 'px) rotate(10deg) scale(1.3)', opacity: 1, offset: 0.45 },
          { transform: 'translate(' + dx + 'px,' + (dy - 6) + 'px) rotate(0deg) scale(0.9)', opacity: 1, offset: 0.86 },
          { transform: 'translate(' + dx + 'px,' + (dy + 4) + 'px) rotate(0deg) scale(0.35)', opacity: 0 }
        ], { duration: ms, easing: 'cubic-bezier(.3,.7,.35,1)', fill: 'forwards' });
        anim.onfinish = function(){ try { t.remove(); } catch(_){} resolve(); };
        setTimeout(function(){ try { t.remove(); } catch(_){} resolve(); }, ms + 200);
      } catch(_){ resolve(); }
    });
  }
  function gulp(box){
    if(!box) return;
    try {
      box.animate([
        { transform: 'scale(1)' }, { transform: 'scale(1.18) rotate(-4deg)', offset: 0.35 },
        { transform: 'scale(0.96) rotate(2deg)', offset: 0.7 }, { transform: 'scale(1)' }
      ], { duration: 420, easing: 'ease-out' });
    } catch(_){}
    var sparks = box.querySelector('.hoh-tkgive-sparks');
    if(!sparks) return;
    sparks.innerHTML = '';
    for(var i = 0; i < 7; i++){
      var s = document.createElement('i');
      var ang = (-160 + i * (140 / 6)) * Math.PI / 180;
      s.style.setProperty('--dx', Math.round(Math.cos(ang) * 22) + 'px');
      s.style.setProperty('--dy', Math.round(Math.sin(ang) * 22) + 'px');
      s.style.animationDelay = (i * 18) + 'ms';
      sparks.appendChild(s);
    }
    box.classList.remove('is-gulp'); void box.offsetWidth; box.classList.add('is-gulp');
    setTimeout(function(){ try { box.classList.remove('is-gulp'); sparks.innerHTML = ''; } catch(_){} }, 900);
  }
  var FAIL_COPY = {
    not_owner: 'Only the account that added this hero can give it tickets.',
    /* R793 QA: the door's answer for a hero that is hidden or not approved yet */
    not_in_draw: 'This hero isn’t showing in the Hall right now, so it can’t take tickets.',
    no_hero: 'This hero isn’t showing in the Hall right now, so it can’t take tickets.',
    bad_account: 'Sign in again, then try once more.',
    pin_required: 'Sign in again, then try once more.',
    sign_in_required: 'Sign in to give your hero today’s ticket.',
    network: 'That did not go through. Try again in a moment.',
    still_loading: 'The Hall is still loading. Try again in a moment.'
  };

  window.hohHallGiveTicket = function(id, btn){
    var d = D();
    if(!d || !id) return;
    var panel = btn && btn.closest ? btn.closest('.hoh-tkgive') : panelsFor(id)[0];
    if(!panel || panel.classList.contains('is-giving') || panel.classList.contains('is-given')) return;
    var num = panel.querySelector('[data-tkgive-num]');
    var sub = panel.querySelector('[data-tkgive-sub]');
    var rise = panel.querySelector('[data-tkgive-rise]');
    var box = panel.querySelector('.hoh-tkgive-box');
    var liveEl = panel.querySelector('[data-tkgive-live]');
    var before = chanceOf(id);
    panel.classList.add('is-giving');
    if(btn){ btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    var rm = reduced();
    /* the ticket leaves on the tap; the door answers while it is in the air */
    var flight = rm ? Promise.resolve() : flyTicket(btn || panel, box || panel, 620);
    var answer = d.tickets.apply(id);
    Promise.all([answer, flight]).then(function(res){
      var r = res[0] || {};
      panel.classList.remove('is-giving');
      if(btn){ btn.removeAttribute('aria-busy'); }
      if(r.missing){ panel.classList.add('is-hidden'); return; }
      if(!r.ok){
        if(r.applied_today){
          /* already given today (another tab or device): show the day as done */
          paintPanel(panel, { chance: (r.chance != null ? r.chance : (before ? before.chance : 0)), tickets: (r.tickets != null ? r.tickets : (before ? before.tickets : 0)), applied: true, next_at: r.next_at });
          return;
        }
        if(btn) btn.disabled = false;
        if(sub) sub.textContent = FAIL_COPY[r.reason] || 'That ticket did not go through. Try again in a moment.';
        try { if(box) box.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(0)' }], { duration: 260 }); } catch(_){}
        return;
      }
      var to = (r.chance != null) ? Number(r.chance) : (r.pool ? (r.tickets / r.pool) : 0);
      var from = before ? before.chance : ((r.pool > 1 && r.tickets > 0) ? ((r.tickets - 1) / (r.pool - 1)) : 0);
      /* the Hall's own copy of the hero follows the door's count */
      var ch = findHero(id); if(ch && r.tickets != null) ch.tickets = r.tickets;
      try {
        var tkCount = document.getElementById('hoh-detail-tk-count');
        var pill = document.getElementById('hoh-detail-tk-pill');
        if(tkCount && pill && pill.getAttribute('data-character-id') === String(id) && r.tickets != null) tkCount.textContent = String(r.tickets);
      } catch(_){}
      var dec = d.chanceDecimals(from, to);
      var gain = Math.max(0, (to - from) * 100);
      if(rise) rise.textContent = '+' + gain.toFixed(dec) + '%';
      if(liveEl) liveEl.textContent = 'Ticket given. Chance to win is now ' + d.formatChance(to, dec) + '.';
      var finish = function(){
        paintPanel(panel, { chance: to, tickets: r.tickets | 0, applied: true, next_at: r.next_at }, { keepNumber: true });
        if(num) num.textContent = d.formatChance(to, dec);
      };
      if(rm){
        /* same beat, nothing moving: the new number and a still highlight */
        if(num) num.textContent = d.formatChance(to, dec);
        panel.classList.add('is-bumped-still');
        setTimeout(function(){ finish(); }, 900);
        setTimeout(function(){ try { panel.classList.remove('is-bumped-still'); } catch(_){} }, 2600);
        return;
      }
      gulp(box);
      panel.classList.remove('is-bumped'); void panel.offsetWidth; panel.classList.add('is-bumped');
      countUp(num, from, to, 900);
      setTimeout(finish, 960);
      setTimeout(function(){ try { panel.classList.remove('is-bumped'); } catch(_){} }, 1700);
    });
  };

  /* A ticket given in one view (card) shows in the other (detail) too. */
  try {
    document.addEventListener('hoh:hall-tickets-changed', function(e){
      var dd = (e && e.detail) || {};
      if(dd.id == null) return;
      var list = panelsFor(dd.id);
      for(var i = 0; i < list.length; i++){
        if(list[i].classList.contains('is-giving') || list[i].classList.contains('is-bumped') || list[i].classList.contains('is-bumped-still')) continue;
        var info = chanceOf(dd.id); if(info) paintPanel(list[i], info);
      }
    });
  } catch(_){}
})();
